import {
  ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input,
  NgZone, OnInit, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild,
  ViewContainerRef, ViewEncapsulation
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import * as d3 from 'd3';
import { FilterService } from 'src/app/services/filter.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { DynamicCustomTooltipService } from 'src/app/services/dynamic-custom-tooltip.service';
import { RouterHandlerService } from 'src/app/services/router-handler.service';
import { ActivatedRoute } from '@angular/router';
import { BasicPopupGridComponent, BasicPopupGridDialogData } from 'src/app/components/shared/grid/basic-popup-grid/basic-popup-grid.component';

/* ──────────────────── Interfaces ──────────────────── */
interface SPNode   { id: string; name: string; n: number; color: string; }
interface HUBNode  { id: string; name: string; n: number; color: string; }
interface FillNode { id: string; name: string; n: number; color: string; hub?: string; }
interface Lane     { id: string; name: string; n: number; pct: number; color: string; bg: string; fillGroup: string; hub?: string; }
interface Link     { s: string; t: string; v: number; }
interface Cfg      { median: number; atRisk: number; maxDays: number; }

interface Patient { id?: string; sp: string; hub: string; lane: string; fillGroup: string; days: number; }

export interface SankeyBeeswarmData {
  total: number;
  sps: SPNode[];
  hubs: HUBNode[];
  fills: FillNode[];      // empty when no bridge role is bound
  lanes: Lane[];          // empty when no lane role is bound
  sp_hub: Link[];
  hub_fill: Link[];       // empty when no bridge role is bound
  hub_lane?: Link[];      // populated only when bridge is skipped (N=3)
  cfg: Cfg;
  patients?: Patient[];
}

interface Dot {
  id: string; sp: string; hub: string; lane: string; fillGroup: string;
  days: number; originalDays: number; color: string; x: number; y: number;
  fx?: number; fy?: number; vx?: number; vy?: number;
  // Full-range layout anchor (set once after the force sim). The brush zoom
  // remaps the display x from baseX through the focus scale; baseY is constant.
  baseX?: number; baseY?: number;
}

interface LaneRect { id: string; y: number; h: number; }
interface NodePos  { id: string; y: number; h: number; color: string; name: string; n: number; pct: number; hub?: string; }
interface SankeyLink {
  s: string; t: string; v: number; color: string;
  x0: number; y0: number; h0: number;
  x1: number; y1: number; h1: number;
}

/* ──────────────────── Constants ──────────────────── */
const W = 1428, DEFAULT_H = 640;
// MARGIN.top is generous so each bar can carry a 2-line label (pct +
// count, then name) above it AND so the "Median time to fill" caption
// floats above the lane stub-bar labels without overlapping them.
const MARGIN = { top: 75, right: 28, bottom: 48, left: 8 };
// Column x-positions: SP → HUB → FILL (bridge) → LANE (per-outcome stub) → Beeswarm.
// SP sits flush at the left margin since labels now render above the bar (not to its left).
const COL_SP = 8, COL_HUB = 256, COL_FILL = 405, COL_LANE = 540;
const BAR_W = 8, DOT_R = 5;
const BEE_LEFT = COL_LANE + BAR_W;
const BEE_RIGHT = W - MARGIN.right;
// Bottom axis and beeswarm dots both start at the same x, just to the right of
// the lane stub bar. Offset is DOT_R + 4 so the leftmost dot (centered on
// DOT_X_START) clears the bar by ~4px instead of overlapping it.
const DOT_X_START = BEE_LEFT + DOT_R + 4;
// Vertical breathing room between the bottom of the lowest lane and the x-axis
// baseline (shrinks `usableH`; the x-axis stays anchored to MARGIN.bottom).
// Kept small now that lane bars are value-proportional with no downward boost.
const AXIS_GAP = 24;
// Vertical gaps between stacked lane stub bars. Sized to clear each lane's
// 2-line above-bar label so neighbours never overlap. Lane bars are now
// value-proportional with NO fixed boost, so the gap no longer reserves boost
// room. WITHIN = lanes in the same fill group; BETWEEN = across fill groups /
// hubs. Shared by computeLayouts AND the H getter (which grows the canvas so
// all lanes + these gaps fit without overlapping).
const LANE_WITHIN_GAP  = 40;
const LANE_BETWEEN_GAP = 50;
// Hard ceiling on the canvas height. The chart grows with lane count up to
// this; beyond it, computeLayouts compresses the lane gaps/boost to fit so
// nothing overlaps instead of growing taller. Adjustable.
const MAX_CANVAS_H = 900;
// Minimum drawn height for a downstream node (fill / outcome) so a lane with
// just 1 patient still reads as a bar (not a single line). Node heights stay
// value-proportional ABOVE this floor.
const MIN_NODE_H = 20;

// Day-range context brush — the horizontal range selector that sits BELOW the
// x-axis (mirrors the line-chart's focus+context brushGroup). The SVG viewBox
// is grown by DAY_BRUSH_BAND_H in render() so this strip has room under the
// axis caption without overlapping the chart above it.
const DAY_BRUSH_GAP = 12;   // gap below the x-axis caption before the strip
const DAY_BRUSH_H   = 22;   // height of the draggable brush track
// Smallest allowed brush window (px). Used as the floor when dragging and as
// the fallback default width when there aren't enough points to size the
// default window by data (see DAY_BRUSH_DEFAULT_POINTS).
const DAY_BRUSH_MIN_PX = 40;
// Default brush window on first load is sized to hold roughly this many data
// points (the lowest-day N patients), anchored at day 0 — so the chart opens
// on the densest low-day region. When the dataset has ≤ this many points the
// default is the full range.
const DAY_BRUSH_DEFAULT_POINTS = 300;
// Total reserved band, measured from the chart's H down to just past the brush
// track. trackTop (in drawDayBrush) = H - MARGIN.bottom + 40 + GAP + 8; the band
// is that plus the track height and a little padding for the handle drop-shadow,
// so the brush sits flush at the bottom of the scale (no empty strip below it).
const DAY_BRUSH_BAND_H = (40 - MARGIN.bottom) + DAY_BRUSH_GAP + 8 + DAY_BRUSH_H + 8;

/* All colour palette + text-label configuration lives in the component's
 * instance `props` object further down (see `props = { labels: {...},
 * colors: {...} }`). Edit values there to retheme the chart or change any
 * user-facing string in one place. The class methods
 * `resolveOutcomeColor` / `resolveOutcomeBg` read from `this.props.colors`. */

@Component({
    selector: 'app-sankey-beeswarm-chart',
    templateUrl: './sankey-beeswarm-chart.component.html',
    styleUrls: ['./sankey-beeswarm-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SankeyBeeswarmChartComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<SVGSVGElement>;
  @ViewChild('fs') fs!: ElementRef;
  @ViewChild('bubbleTooltipTpl', { static: true }) bubbleTooltipTpl!: TemplateRef<any>;
  @ViewChild('nodeTooltipTpl',   { static: true }) nodeTooltipTpl!: TemplateRef<any>;
  @ViewChild('lassoPanelTpl',    { static: true }) lassoPanelTpl!: TemplateRef<any>;
  @HostBinding('class.is-fullscreen') isFullscreen = false;

  @Input() data: any = [];
  @Input() triageTotaldata: any;
  @Input() pageKey: any;
  @Input() item: any;
  @Input() config: any;
  @Input() headerConfig: any;
  @Input() heading: string = '';
  @Input() error_name: string = '';
  /** Optional chart height (SVG viewBox height). Defaults to 640. */
  @Input() height?: number;

  /** Resolved chart height — input or default, except in fullscreen mode
   *  where we grow the viewBox height to match the viewport's aspect ratio
   *  so the rendered SVG fills the screen instead of the small embedded box. */
  /** Base canvas height — the "normal" height (input / default, or the
   *  viewport-fitted height in fullscreen) BEFORE any growth for lane volume.
   *  The SP (level 1) and HUB columns are sized against this so they don't
   *  stretch just because the beeswarm/outcome side has lots of data. */
  /** Extra viewBox height reserved for the day-range brush strip (0 when the
   *  brush is off / there's no beeswarm). The render() viewBox is H + this, so
   *  fullscreen sizing must subtract it to keep the strip on-screen. */
  private get dayBrushBand(): number {
    const showBrush = this.roleLevelIndex.lane !== null && this.props.config.showDayBrush !== false;
    return showBrush ? DAY_BRUSH_BAND_H : 0;
  }

  private get baseH(): number {
    if (this.isFullscreen && typeof window !== 'undefined') {
      // Size from the ACTUAL space so the chart fills the full screen height:
      // measure the real top-bar height (container's offset inside the
      // fullscreen article) and the container width, then make the SVG (width
      // 100%, height auto) render exactly into the remaining height. The viewBox
      // is H + the brush band, so subtract the band here or the strip renders
      // below the fold (not visible in fullscreen).
      const container = this.canvasRef?.nativeElement?.parentElement as HTMLElement | null;
      const fsEl = this.fs?.nativeElement as HTMLElement | undefined;
      if (container && fsEl && container.clientWidth > 0 && fsEl.clientHeight > 0) {
        const topBar = container.getBoundingClientRect().top - fsEl.getBoundingClientRect().top;
        const availableH = Math.max(300, fsEl.clientHeight - topBar - 8);
        return Math.max(200, Math.round(W * availableH / container.clientWidth) - this.dayBrushBand);
      }
      // Fallback before the fullscreen layout has settled.
      const w = window.innerWidth || 1;
      const availableH = Math.max(400, window.innerHeight - 140);
      return Math.max(200, Math.round(W * availableH / w) - this.dayBrushBand);
    }
    return (typeof this.height === 'number' && this.height > 0) ? this.height : DEFAULT_H;
  }

  private get H(): number {
    const base = this.baseH;

    // In fullscreen the base height is already sized to FIT the viewport, so we
    // must NOT grow past it — that would push the bottom lanes off-screen.
    // computeLayouts compresses the lane gaps to fit the fixed height instead.
    if (this.isFullscreen) return base;

    // Grow the canvas so every lane — plus the gaps that keep its boosted bar
    // and 2-line label from overlapping its neighbour — fits. With per-hub
    // downstream there can be many lanes; a fixed height would force the big
    // ones to collapse and overlap. ONLY the level-4 / beeswarm side expands:
    // SP (level 1) and HUB stay sized to `baseH` (see computeLayouts). Node
    // heights stay value-proportional; we only guarantee the proportional
    // budget (laneBudget) is positive. Mirrors the gap logic in computeLayouts.
    const lanes = this.chartData?.lanes;
    if (!lanes || lanes.length === 0) return base;
    let gaps = 0;
    for (let i = 1; i < lanes.length; i++) {
      gaps += (lanes[i - 1].fillGroup === lanes[i].fillGroup) ? LANE_WITHIN_GAP : LANE_BETWEEN_GAP;
    }
    // Budget = a guaranteed minimum per lane + a proportional pool shared by
    // value. Ensures every lane clears MIN_NODE_H while bigger lanes still get
    // proportionally taller.
    const barsBudget = lanes.length * MIN_NODE_H + Math.max(260, lanes.length * 24);
    const needed = MARGIN.top + MARGIN.bottom + AXIS_GAP + gaps + barsBudget;
    // Grow toward `needed` but never past the cap (or the explicit base height,
    // whichever is larger). When capped, computeLayouts compresses gaps to fit.
    const cap = Math.max(base, MAX_CANVAS_H);
    return Math.min(cap, Math.max(base, needed));
  }

  isActive = false;
  iconList: any[] = [];
  isLoading = true;
  noData = false;
  initiateChart = false;

  /* ── Chart state ── */
  // Multi-select legend / node filters. Each Set tracks every id the user
  // has toggled ON in that column — selections combine across columns
  // (union of all active routes is highlighted).
  activeSPs   = new Set<string>();
  activeHubs  = new Set<string>();
  activeLanes = new Set<string>();
  lassoSelected = new Set<string>();
  hintVisible = true;

  /** Structured data bound into the lasso-panel CDK overlay template. */
  lassoPanelData: {
    count: number;
    avgDays: number;
    medianDays: number;
    atRisk: number;
    bySP:   Array<{ id: string; name: string; color: string; count: number; barPct: number }>;
    byLane: Array<{ id: string; name: string; color: string; count: number; barPct: number }>;
  } | null = null;
  /** Dedicated overlay for the lasso panel — separate from the hover-tooltip
   *  overlay in DynamicCustomTooltipService so the panel survives while the
   *  user hovers dots / bars and sees their tooltips. */
  private lassoPanelOverlay: OverlayRef | null = null;

  /* ── Legend data ── */
  spList: SPNode[] = [];
  fillList: FillNode[] = [];
  // Outcome legend deduped by NAME (fills are per-hub, so e.g. "Filled"
  // exists once per hub; the legend shows a single pill that toggles every
  // hub-scoped fill of that name).
  fillLegend: Array<{ name: string; color: string; n: number; ids: string[] }> = [];

  /* ── Tooltip ── */
  /** Data bound into the bubble (dot) tooltip template. */
  bubbleTooltipData: {
    color: string;
    spName: string;
    laneName: string;
    days: number;
    atRisk: boolean;
    patientId: string;
  } | null = null;

  /** Data bound into the generic node (SP / HUB / BRIDGE / LANE / sankey link) tooltip.
   *  Shape mirrors the sankey-chart component for visual consistency:
   *  a header strip on top + a list of bullet items in the body. */
  nodeTooltipData: {
    header: string;
    items: Array<{ bulletColor: string; text: string }>;
  } | null = null;

  /* Private overlay for bubble (dot) tooltips. Keeping this separate from the
   * shared DynamicCustomTooltipService.overlayRef avoids dispose/recreate
   * thrash when the mouse moves between dots and prevents collisions with
   * the node-tooltip / lasso-panel overlays. */
  private bubbleOverlay: OverlayRef | null = null;
  private bubbleHideTimer: any = null;
  /** True while the user is actively dragging a lasso. During the drag we
   *  suppress dot hover handlers — otherwise mouseenter on every dot the
   *  cursor passes through fires raise/highlight + tooltip on top of the
   *  lasso's own dot-highlight, producing overlapping visual states. */
  private isLassoDragging = false;

  /* ── Internal ── */
  chartData!: SankeyBeeswarmData;
  private svg: any;
  private dots: Dot[] = [];
  private laneRects: LaneRect[] = [];
  private spNodes: NodePos[] = [];
  private hubNodes: NodePos[] = [];
  private bridgeBars: NodePos[] = [];
  private laneBars: NodePos[] = [];
  // Dynamic width (px) of the "≤ 0 day" zone at the left of the beeswarm. All
  // zero/negative-day dots live in this band, to the LEFT of the 0d tick;
  // positive days start at the tick. Sized in computeLayouts from the zero
  // counts so they never spill past 0d.
  private zeroBandWidth = 0;
  private spHubLinks: SankeyLink[] = [];
  private hubFillLinks: SankeyLink[] = [];
  private fillLaneLinks: SankeyLink[] = [];
  // Populated only in the bridge-skipped (N=3) case so the engaged-hub bar
  // can ribbon straight into each lane stub. Always empty for N=4.
  private hubLaneLinks: SankeyLink[] = [];
  private lassoPoints: [number, number][] = [];

  // ── Day-range context brush state (focus+context, like d3-basic-line-popup) ──
  // The strip below the x-axis is the CONTEXT (always the full day range); the
  // main beeswarm is the FOCUS. Dragging the brush rescales the focus to the
  // selected day window and re-lays-out the dots, so the data "moves"/zooms.
  private dayBrush: any = null;            // the d3.brushX behaviour
  private dayBrushGroup: any = null;       // the <g> the brush is bound to
  private suppressDayBrushEvent = false;   // guards programmatic brush.move()
  // Current FOCUS window in days. null === full range (the default "select all"
  // view). Set from the brushed pixel window via the overview scale.
  private dayDomain: [number, number] | null = null;
  // Fixed CONTEXT scale (full [0, maxDays] → strip width). Used to invert the
  // brushed pixels back to a day window. Built in drawDayBrush.
  private dayOverviewScale: d3.ScaleLinear<number, number> | null = null;
  private dayTrackTop = 0;                 // y of the brush track (for handle decorations)
  // When set, the focus-dependent layers (at-risk band / median line / x-axis)
  // append into this group instead of the root svg, so applyFocusTransform can
  // clear + redraw them in place on every brush event without disturbing
  // z-order (they sit behind the dots).
  private focusBgTarget: any = null;
  // Full SVG viewBox height = chart H + the day-brush band (when a lane /
  // beeswarm exists). Used for screen↔SVG coordinate mapping so the lasso
  // stays aligned after the viewBox grew to fit the brush strip.
  private viewBoxH = 0;

  // Set during buildChartData so logNodeNavigation can apply the same
  // top-N rollup constraint the visual SP aggregate uses.
  private namedSPNames = new Set<string>();

  // Role bindings derived from `props.fields.levels` length so the chart
  // adapts to N ∈ {2, 3, 4} without per-call branching. A null role means
  // that column is skipped in aggregation / layout / drawing:
  //   N=2 → sp=0, hub=1, bridge=null, lane=null      (no beeswarm)
  //   N=3 → sp=0, hub=1, bridge=null, lane=2          (SP→HUB→Lane direct)
  //   N=4 → sp=0, hub=1, bridge=2,    lane=3          (canonical)
  private roleLevelIndex: { sp: number; hub: number; bridge: number | null; lane: number | null } = {
    sp: 0, hub: 1, bridge: 2, lane: 3
  };

  moduleName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;

  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') this.clearLasso();
  }

  @HostListener('fullscreenchange')
  @HostListener('webkitfullscreenchange')
  screenChange() {
    // Authoritative fullscreen state + re-render are driven by the actual
    // fullscreenchange event (fired once the browser has applied fullscreen and
    // finalized the window/element dimensions). This avoids the first-entry
    // blink that timer-based rendering caused when the size wasn't ready yet.
    const fs = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
    this.isFullscreen = fs;
    this.isActive = fs;
    this.rerenderForFullscreen();
  }

  constructor(
    private filterService: FilterService,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private dynamicTooltipService: DynamicCustomTooltipService,
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
    private routerHandlerService: RouterHandlerService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {
    this.route.params.subscribe((p: any) => {

      this.moduleName = p['module'] || '';

      this.route.queryParams.subscribe((params: any) => {
        let decryptedParams = this.navigationService.decryptData(params);

        this.clientKey = decryptedParams["cl_key"];
        this.moduleKey = decryptedParams["md_key"];
        this.dashboardKey = decryptedParams["ds_key"];
      });
    });
  }

  /* ──────────────────────────────────────────────────────────────────────
   *  CHART CONFIGURATION
   *  All user-facing labels + colour palette live here so a future theme /
   *  copy change is a single-place edit. Everything below in the chart
   *  reads from `this.props.*` rather than inline strings or hex codes.
   * ────────────────────────────────────────────────────────────────────── */
  props = {
    /** Text strings rendered inside the SVG / used to format axis ticks. */
    labels: {
      /** Sits above the dashed vertical median reference line. */
      medianLine:       'Median time to fill',
      /** Title above the red "at risk" overlay band (Pending lane only). */
      atRiskBand:       'At Risk of Abandonment',
      /** Caption under the bottom x-axis scale. */
      xAxisCaption:     'Engagement to Outcome Duration',
      /** Tiny "time" word that appears just left of the first axis tick. */
      timeAxisLabel:    'time',
      /** Suffix shown inside the highlighted median tick pill ("11 days"). */
      daysSuffix:       'days',
      /** Suffix for every other axis tick ("25 d", "50 d", …). */
      daysShortSuffix:  'd',
      /** Display name for the "Other SPs" rollup bucket. */
      otherSP:          'Other SPs',
      /** Display name when a row has no fill value (used as fill node label). */
      notFilledName:    'Not Filled',
    },

    /** Field-name mapping for the incoming data rows. Edit these keys to
     *  point the chart at a different schema — every other place in this
     *  file reads from `this.props.fields.*` rather than hard-coding any
     *  particular column name. */
    fields: {
      // Ordered Sankey columns (left → right). The chart's column count is
      // driven by this array length, so a 2-group setup
      // (['level_1','level_2']) renders a 2-column Sankey and a 5-group
      // setup adds another column on the right — no per-level code edits
      // here. Both buildChartData and logNodeNavigation walk this same
      // array so the visual grouping and the per-node patient-id lookup
      // can never go out of sync.
      levels:    ['level_1', 'level_2', 'level_3', 'level_4'] as string[],
      patientId: 'du_patient_id',
      time:      'time',
      count:     'patient_count',   // optional, defaults to 1 per row
    },

    /** A few simple presentation / statistical knobs. The chart auto-detects
     *  every structural identifier (which hub has downstream outcomes, which
     *  outcomes exist, which fill groups exist) directly from the input
     *  rows — no `engagedHubId` / `filledFillId` / etc. to keep in sync. */
    config: {
      /** How many SPs to render by NAME before grouping the remainder into
       *  the `labels.otherSP` rollup. */
      topSPCount: 4,
      /** Controls which lane(s) get the at-risk / Abandonment overlay band:
       *    'all'      — paint the band over EVERY lane past the median (a
       *                 "time-to-anything" view showing risk across all
       *                 outcomes).
       *    'specific' — only paint over lanes whose id is listed in
       *                 `atRiskLaneId` OR whose name matches any pattern in
       *                 `atRiskLaneNamePattern`.
       *  Defaults to 'specific' so the historical Pending-only behaviour
       *  remains the out-of-the-box default. */
      atRiskMode: 'specific' as 'all' | 'specific',
      /** Comma-separated list of lane ids that qualify for the at-risk band.
       *  Single lane today (`'pending'`); multi-lane works the same way
       *  (`'pending,cancelled_at_hub'`). Ignored when `atRiskMode === 'all'`. */
      atRiskLaneId:           'pending',
      /** Comma-separated list of name regex patterns (case-insensitive) used
       *  in addition to `atRiskLaneId`. A lane qualifies if ANY id OR ANY
       *  pattern matches. Ignored when `atRiskMode === 'all'`. */
      atRiskLaneNamePattern:  'pending',
      /** Median fallback (days) when no rows with `time` exist. */
      medianDaysFallback: 11,
      /** Default p90 (days) when there are no patients with `time` values. */
      p90DaysFallback:    60,
      /** Whether the day-range zoom brush (the strip below the x-axis) is
       *  shown. `false` hides it entirely — the beeswarm renders at full range
       *  and the SVG keeps its original height (no band reserved). Driven by
       *  the widget config in `applyItemConfig` so it can be toggled per-chart. */
      showDayBrush: true,
    },

    /** Centralised colour palette for every node + branch + overlay in the
     *  chart. Branches (sankey ribbons) inherit their colour from the TARGET
     *  node, so each ribbon visually carries the colour of where it flows
     *  INTO. */
    colors: {
      /** Flat colour map for every level_1 / level_2 / level_3 / level_4
       *  value rendered in the chart. Keys are the RAW STRINGS from the
       *  data (level values as they appear in the rows), so a future
       *  column / value change is a one-line edit here — no toId / casing
       *  juggling. Anything not listed falls back to `defaultColor` (or
       *  to `fallbackPalette` by index for top-N SPs). */
      values: {
        // level_1 (SPs) — exactly as written in data.json
        'Biologics':         '#0934CC',
        'Onco360':           '#E262FC',
        'Amber':             '#4AB1FF',
        'Other SPs':         '#FDCB6E',   // matches `labels.otherSP`

        // level_2 (hubs)
        'HUB Engaged':       '#9074FF',
        'HUB Not Engaged':   '#8A98AB',

        // level_3 (fill status)
        'Filled':            '#2EC887',
        'Not Filled':        '#FF4567',   // matches `labels.notFilledName`
        
        'Filled At SP':      '#2EC887',   // matches `labels.notFilledName`
        'Not Filled At SP':  '#FF4567',   // matches `labels.notFilledName`

        // level_4 (outcomes)
        'Paid Claims':       '#2EC887',
        'PAP':               '#2EC887',
        'Bridge/QS':         '#2EC887',
        'Cancelled At HUB':  '#FF4567',
        'Pending':           '#F7AF00',
      } as Record<string, string>,

      /** Soft swimlane backgrounds keyed by the raw level_4 string. */
      backgrounds: {
        'Paid Claims':       '#E8F5E9',
        'PAP':               '#E8F5E9',
        'Bridge/QS':         '#E8F5E9',
        'Cancelled At HUB':  '#FFEBEE',
        'Pending':           '#FFFDE7',
      } as Record<string, string>,

      /** Auto-assigned palette used when a value isn't listed in `values`.
       *  Any unmatched name picks the next colour from this list, and the
       *  assignment is cached per render so the same value keeps the same
       *  colour everywhere it appears. Long enough to handle charts with
       *  many unbranded categories without collisions. */
      fallbackPalette: [
        '#E74028', '#2CB239', '#25D3EB', '#FFB039', '#1566DE', '#7848FF',
        '#7AD045', '#CB04DC', '#FA195C', '#9E0000', '#EACD37', '#DD630B',
        '#BCD03F', '#14BDA9', '#8A05F3', '#D9476A', '#2DADB6', '#E468C2',
        '#199CD4', '#0934CC',
        '#e74c3c', '#3498db', '#2ecc71', '#f1c40f', '#9b59b6',
        '#1abc9c', '#34495e', '#e67e22', '#16a085', '#d35400',
        '#2980b9', '#8e44ad', '#c0392b', '#27ae60', '#f39c12',
        '#7f8c8d', '#e74c3c', '#3498db', '#2ecc71', '#f1c40f',
        '#9b59b6', '#1abc9c', '#34495e', '#e67e22', '#16a085',
        '#d35400', '#2980b9', '#8e44ad', '#c0392b', '#27ae60',
        '#f39c12', '#7f8c8d', '#e74c3c', '#3498db', '#2ecc71',
        '#f1c40f', '#9b59b6', '#1abc9c', '#34495e', '#e67e22',
        '#16a085', '#d35400', '#2980b9', '#8e44ad', '#c0392b',
        '#27ae60', '#f39c12', '#7f8c8d',
      ] as string[],

      /** Generic fallbacks when nothing matches in the maps above. */
      defaultColor:      '#636e72',
      defaultBackground: '#f5f6fa',

      /** Median reference line + its label colour. */
      medianLine:     '#ff7675',
      medianText:     '#636e72',
      /** Highlighted median tick pill (the "{n} days" capsule on the axis). */
      medianPillFill: '#636e72',
      medianPillText: '#fff',

      /** Red "Abandonment" overlay band + its title text. */
      atRiskBand:  '#ff7675',
      atRiskText:  '#ff7675',

      /** Bottom x-axis line, tick text, and caption text. */
      axisLine:        '#dfe6e9',
      axisTickText:    '#b2bec3',
      axisCaption:     '#b2bec3',
      axisTickStrong:  '#636e72',  // colour of the median tick line on the axis
    },
    valueNodeOrder: {
      level_1: ['Biologics', 'Onco360', 'Amber'],  // SPs in this order, then "Other SPs"
      level_2: ['HUB Engaged', 'HUB Not Engaged'], // HUB Engaged above Not Engaged
      level_3: ['Filled', 'Not Filled', 'Filled At SP', 'Not Filled At SP'],           // Filled above Not Filled
      level_4: ['Paid Claims', 'PAP', 'Bridge/QS', 'Cancelled At HUB', 'Pending', 'Filled', 'Not Filled'], // outcomes in this order
    }
  };

  /** Tracks the auto-assigned colour for any name that wasn't found in
   *  `props.colors.values`. Cleared at the start of each `buildChartData`
   *  call so the same render always produces the same colour mapping. */
  private autoColorCache = new Map<string, string>();

  /** Single colour lookup used everywhere in the chart. Resolution order:
   *    1. `props.colors.values[name]` — explicit brand colour
   *    2. cached auto-assignment from `props.colors.fallbackPalette`
   *    3. (if no palette) `props.colors.defaultColor`
   *  The cache means the same raw name always renders in the same colour
   *  regardless of which level / column it appears under. */
  private getColor(name: string): string {
    const c = this.props.colors;
    if (!name) return c.defaultColor;
    const branded = c.values[name];
    if (branded) return branded;
    const cached = this.autoColorCache.get(name);
    if (cached) return cached;
    const palette = c.fallbackPalette || [];
    if (palette.length === 0) return c.defaultColor;
    const auto = palette[this.autoColorCache.size % palette.length];
    this.autoColorCache.set(name, auto);
    return auto;
  }

  /** Resolve an outcome's LANE colour. Outcome first, then the raw fill
   *  name as a secondary hint, then the shared `getColor` auto-assign. */
  private resolveOutcomeColor(laneName: string, fillName: string): string {
    const c = this.props.colors;
    if (c.values[laneName]) return c.values[laneName];
    if (c.values[fillName]) return c.values[fillName];
    return this.getColor(laneName);
  }

  /** Resolve an outcome's swimlane BACKGROUND colour from the raw name. */
  private resolveOutcomeBg(laneName: string): string {
    const c = this.props.colors;
    return c.backgrounds[laneName] || c.defaultBackground;
  }

  ngOnInit(): void {
    this.applyItemConfig();
    this.isLoading = true;
    this.data = [];

    this.filterService.filterQuery.subscribe((query: any) => {
      this.isLoading = true;
      this.data = [];
      // A filter change brings a fresh dataset, so drop any state tied to the
      // OLD data: the day-brush zoom window and any active legend / lasso
      // selections. With dayDomain cleared, the next render recomputes the
      // default (300-point) brush window + beeswarm for the new data instead
      // of keeping the previous selection.
      this.dayDomain = null;
      this.activeSPs.clear();
      this.activeHubs.clear();
      this.activeLanes.clear();
      this.clearLassoOnly();
    })
    // Data comes via @Input, render is triggered by ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes && changes?.["data"] && changes["data"].currentValue != changes["data"].previousValue) {
      this.isLoading = true;
      this.data = changes["data"].currentValue || [];
      this.processAndRender();
    }
  }

  /** Overlay values from the widget configuration (`this.item.config`) onto
   *  the chart's `props` (labels / fields / colors). Anything not present in
   *  the config keeps its existing default, so the chart still works when
   *  invoked without a backend-driven config. */
  private applyItemConfig(): void {
    const cfg = this.item?.config;
    if (!cfg) return;

    if (cfg.medianLine)   this.props.labels.medianLine   = cfg.medianLine;
    if (cfg.atRiskBand)   this.props.labels.atRiskBand   = cfg.atRiskBand;
    if (cfg.xAxisCaption) this.props.labels.xAxisCaption = cfg.xAxisCaption;

    if (Array.isArray(cfg.level_selection) && cfg.level_selection.length > 0) {
      const levels = cfg.level_selection
        .map((l: any) => l?.api_resp_column_nm)
        .filter((v: any) => !!v);
      if (levels.length > 0) this.props.fields.levels = levels;
    }
    if (cfg.day_column?.api_resp_column_nm) {
      this.props.fields.time = cfg.day_column.api_resp_column_nm;
    }
    if (cfg.navigation_column?.api_resp_column_nm) {
      this.props.fields.patientId = cfg.navigation_column.api_resp_column_nm;
    }

    // Day-range zoom brush on/off. Accepts a plain boolean, the string
    // 'true'/'false', or the common `{ value: 'true' }` config shape, under
    // any of these keys. Only flips the default when the config actually
    // provides a value, so omitting it keeps the brush enabled.
    const brushCfg = cfg.day_brush ?? cfg.show_brush ?? cfg.zoom_brush;
    if (brushCfg !== undefined && brushCfg !== null) {
      const v = (typeof brushCfg === 'object') ? brushCfg.value : brushCfg;
      this.props.config.showDayBrush = !(v === false || v === 'false' || v === 0 || v === '0');
    }

    const colorVals = cfg.color_configuration?.values;
    if (Array.isArray(colorVals)) {
      colorVals.forEach((v: any) => {
        const key = v?.col_label ?? v?.col_value;
        if (key && v?.color) this.props.colors.values[key] = v.color;
      });
    }
  }

  ngOnDestroy(): void {
    this.dynamicTooltipService.hideMouseTooltip();
    this.disposeLassoPanel();
    if (this.bubbleHideTimer) clearTimeout(this.bubbleHideTimer);
    if (this.fsRenderHandle != null) cancelAnimationFrame(this.fsRenderHandle);
    this.disposeBubbleOverlay();
  }

  /* ────────────────────────────────────────────────── */
  /*  Public methods for template                       */
  /* ────────────────────────────────────────────────── */

  /** Toggle an id in/out of the SP filter set. Other columns are NOT
   *  cleared — multiple selections combine (union) across all columns. */
  toggleSP(id: string) {
    this.clearLasso();
    if (this.activeSPs.has(id)) this.activeSPs.delete(id);
    else                        this.activeSPs.add(id);
    this.applyFilters();
  }

  toggleHub(id: string) {
    this.clearLasso();
    if (this.activeHubs.has(id)) this.activeHubs.delete(id);
    else                         this.activeHubs.add(id);
    this.applyFilters();
  }

  toggleLane(id: string) {
    this.clearLasso();
    if (this.activeLanes.has(id)) this.activeLanes.delete(id);
    else                          this.activeLanes.add(id);
    this.applyFilters();
  }

  isSPActive(id: string): boolean   { return this.activeSPs.has(id); }
  isSPDimmed(id: string): boolean   { return this.hasActiveFilter() && !this.activeSPs.has(id); }
  isLaneActive(id: string): boolean { return this.activeLanes.has(id); }
  isLaneDimmed(id: string): boolean { return this.hasActiveFilter() && !this.activeLanes.has(id); }

  // Legend (deduped-by-name) helpers: toggle / state across all the per-hub
  // fill ids that share a display name.
  toggleFillName(ids: string[]) {
    this.clearLasso();
    const allActive = ids.length > 0 && ids.every(id => this.activeLanes.has(id));
    if (allActive) ids.forEach(id => this.activeLanes.delete(id));
    else           ids.forEach(id => this.activeLanes.add(id));
    this.applyFilters();
  }
  isFillNameActive(ids: string[]): boolean {
    return ids.length > 0 && ids.every(id => this.activeLanes.has(id));
  }
  isFillNameDimmed(ids: string[]): boolean {
    return this.hasActiveFilter() && !ids.some(id => this.activeLanes.has(id));
  }
  private hasActiveFilter(): boolean {
    return this.activeSPs.size > 0 || this.activeHubs.size > 0 || this.activeLanes.size > 0;
  }

  fullscreenChanges(val: any) {
    this.isFullscreen = val;
    this.isActive = val;
    // The H getter returns a different value in fullscreen — re-render so
    // the viewBox + every layout calc picks up the new viewport-fitted height.
    this.rerenderForFullscreen();
  }

  closeFullscreen() {
    this.isFullscreen = false;
    this.isActive = false;
    this.rerenderForFullscreen();
  }

  private fsRenderHandle: any = null;
  /** Re-render when entering / exiting fullscreen so the chart geometry adapts
   *  to the new H. Deferred two animation frames so the browser has finalized
   *  the fullscreen layout + dimensions before we measure (no first-entry size
   *  jump / blink), and debounced so the fullscreenchange event and the
   *  icon-chart's emit collapse into a single render. */
  private rerenderForFullscreen() {
    if (!this.chartData) return;
    if (this.fsRenderHandle != null) cancelAnimationFrame(this.fsRenderHandle);
    this.fsRenderHandle = requestAnimationFrame(() => {
      this.fsRenderHandle = requestAnimationFrame(() => {
        this.fsRenderHandle = null;
        this.render();
      });
    });
  }

  layerControl(ev: any) {}

  /* ────────────────────────────────────────────────── */
  /*  Process data & render                             */
  /* ────────────────────────────────────────────────── */

  private processAndRender() {
    if (!this.data || (Array.isArray(this.data) && this.data.length === 0) || (typeof this.data === 'object' && Object.keys(this.data).length === 0)) {
      // this.noData = true;
      this.data = [];
      this.isLoading = false;
      return;
    }
    this.noData = false;

    // Accept either the raw SankeyBeeswarmData or the API response
    this.chartData = this.buildChartData(this.data);
    this.spList = this.chartData.sps;
    this.fillList = this.chartData.fills;

    // Dedupe the outcome legend by name across the per-hub fill nodes.
    const legendByName = new Map<string, { name: string; color: string; n: number; ids: string[] }>();
    this.fillList.forEach(f => {
      const e = legendByName.get(f.name) || { name: f.name, color: f.color, n: 0, ids: [] };
      e.n += f.n;
      e.ids.push(f.id);
      legendByName.set(f.name, e);
    });
    this.fillLegend = Array.from(legendByName.values());

    setTimeout(() => {
      this.render();
      this.isLoading = false;
      this.initiateChart = true;
    }, 0);
  }

  private buildChartData(raw: any): SankeyBeeswarmData {
    // Pre-aggregated format — use directly
    if (raw.sps && raw.hubs && raw.fills && raw.lanes) return raw;

    const rows: any[] = Array.isArray(raw) ? raw : [];

    // Reset the per-render colour cache so the same data always produces
    // the same auto-assigned colours, but a data change can re-assign.
    this.autoColorCache.clear();

    // ── Normalize row keys via `props.fields`. The only thing this method
    //    knows about the schema is `fields.levels` (ordered column array)
    //    + the patientId/time/count column names. The chart still works in
    //    terms of four ROLES (sp / hub / bridge / lane), but each role is
    //    bound to a level index — or null — based on how many levels the
    //    caller supplied:
    //      N=2 → sp=0, hub=1                       (no bridge, no lane)
    //      N=3 → sp=0, hub=1,            lane=2    (SP→HUB→Lane direct)
    //      N=4 → sp=0, hub=1, bridge=2,  lane=3    (canonical)
    //    Drawers and aggregations skip the layers whose role is null. ──
    const f = this.props.fields;
    const levelKeys = Array.isArray(f.levels) ? f.levels : [];
    const N = levelKeys.length;
    const roles: { sp: number; hub: number; bridge: number | null; lane: number | null } = {
      sp:     0,
      hub:    1,
      bridge: N >= 4 ? 2 : null,
      lane:   N >= 3 ? N - 1 : null,
    };
    const spIdx     = roles.sp;
    const hubIdx    = roles.hub;
    const bridgeIdx = roles.bridge;
    const laneIdx   = roles.lane;
    const norm = (r: any) => {
      const levelValues = levelKeys.map(k => String(r[k] ?? '').trim());
      return {
        levelValues,
        sp:        levelValues[spIdx] || '',
        hub:       N >= 2 ? (levelValues[hubIdx] || '') : '',
        fill:      bridgeIdx !== null ? (levelValues[bridgeIdx] || '') : '',
        outcome:   laneIdx   !== null ? (levelValues[laneIdx]   || '') : '',
        time:      (r[f.time] !== undefined && r[f.time] !== '' ? r[f.time] : null),
        patientId: String(r[f.patientId] ?? ''),
        count:     r[f.count] || 1,
      };
    };
    const data = rows.map(norm).filter(r => r.sp);
    const total = data.reduce((s, r) => s + r.count, 0) || 1;

    // ── Per-level display-order lookup (`props.valueNodeOrder`). Names
    //    listed for a level render in the given order; anything not listed
    //    keeps the column's existing tiebreaker (count desc, etc.).
    //    Keys are the canonical positional names `level_1`..`level_N`
    //    (independent of any backend rename via `fields.levels`); the raw
    //    field name is tried as a fallback. ──
    const nodeOrderMap = (this.props as any).valueNodeOrder as Record<string, string[]> | undefined;
    const orderListFor = (levelIdx: number): string[] => {
      const canonicalKey = `level_${levelIdx + 1}`;
      const fieldKey = levelKeys[levelIdx];
      const arr = nodeOrderMap?.[canonicalKey] ?? nodeOrderMap?.[fieldKey];
      return Array.isArray(arr) ? arr : [];
    };
    const orderRank = (arr: string[], name: string) => {
      const i = arr.indexOf(name);
      return i === -1 ? arr.length : i;
    };

    // ── SP grouping: top-N by TOTAL count + the "Other SPs" rollup. ──
    const spTotals = new Map<string, number>();
    data.forEach(r => spTotals.set(r.sp, (spTotals.get(r.sp) || 0) + r.count));
    const spOrder = orderListFor(spIdx);
    const sortedSPs = Array.from(spTotals.entries()).sort((a, b) => {
      const ra = orderRank(spOrder, a[0]), rb = orderRank(spOrder, b[0]);
      if (ra !== rb) return ra - rb;
      return b[1] - a[1];
    });
    // All structural identifiers come from the DATA itself; only colour /
    // labels / a few presentation knobs come from `this.props`. The chart
    // adapts to whatever level_2 / level_3 / level_4 values the backend
    // ships — no hard-coded "engaged hub" / "filled" ids anywhere.
    const palette  = this.props.colors;
    const lbl      = this.props.labels;
    const cfgKnobs = this.props.config;
    const TOP_SP   = cfgKnobs.topSPCount;

    // Default fill-group id used for rows whose level_3 is null. Derived
    // from `labels.notFilledName` so the placeholder label and the
    // synthesised id stay in lock-step automatically.
    const DEFAULT_FG = this.toId(lbl.notFilledName);

    // ── Detect the "engaged" hub generically (HUB display order only).
    //    `gateIdx` is the LAST fully-populated level; the engaged value is
    //    whichever value at that level has the most rows with a non-empty
    //    deeper level. This is now used ONLY to put the engaged hub first in
    //    the hub column when `valueNodeOrder` doesn't dictate an order — the
    //    downstream fills / lanes / dots aggregate across ALL hubs, so it is
    //    no longer a data gate. ──
    let gateIdx = -1;
    for (let i = 0; i < levelKeys.length; i++) {
      if (data.every(r => !!r.levelValues[i])) gateIdx = i;
      else break;
    }
    const lastIdx = levelKeys.length - 1;
    let engagedHubId = '';
    if (gateIdx >= 0 && gateIdx < lastIdx) {
      const gateCounts = new Map<string, number>();
      data.forEach(r => {
        const hasDownstream = r.levelValues.slice(gateIdx + 1).some((v: string) => !!v);
        if (!hasDownstream) return;
        const id = this.toId(r.levelValues[gateIdx]);
        if (!id) return;
        gateCounts.set(id, (gateCounts.get(id) || 0) + r.count);
      });
      engagedHubId = Array.from(gateCounts.entries())
        .sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    }

    const namedSPNames = new Set(sortedSPs.slice(0, TOP_SP).map(([n]) => n));
    const spId = (name: string) => namedSPNames.has(name) ? this.toId(name) : 'other';
    // ── SP nodes: TOTAL patients per SP (every hub combined, for proper Sankey flow) ──
    const sps: SPNode[] = sortedSPs.slice(0, TOP_SP)
      .map(([name, n]) => ({ id: this.toId(name), name, n, color: this.getColor(name) }))
      .filter(sp => sp.n > 0);
    const otherTotalN = data.filter(r => !namedSPNames.has(r.sp)).reduce((s, r) => s + r.count, 0);
    if (otherTotalN > 0) sps.push({ id: 'other', name: lbl.otherSP, n: otherTotalN, color: this.getColor(lbl.otherSP) });

    // ── Hub nodes — built straight from data. Every unique level_2 value
    //    becomes a hub bar; display name + count come from the rows so
    //    backend casing / wording flows through unchanged. ──
    const hubAgg = new Map<string, { name: string; n: number }>();
    data.forEach(r => {
      const id = this.toId(r.hub);
      if (!id) return;
      const cur = hubAgg.get(id) || { name: r.hub, n: 0 };
      cur.n += r.count;
      hubAgg.set(id, cur);
    });
    const hubOrder = orderListFor(hubIdx);
    const hubs: HUBNode[] = Array.from(hubAgg.entries()).map(([id, info]) => ({
      id, name: info.name, n: info.n,
      color: this.getColor(info.name),
    }))
    // valueNodeOrder wins; otherwise engaged hub first, then count desc.
    .sort((a, b) => {
      const ra = orderRank(hubOrder, a.name), rb = orderRank(hubOrder, b.name);
      if (ra !== rb) return ra - rb;
      if (a.id === engagedHubId) return -1;
      if (b.id === engagedHubId) return 1;
      return b.n - a.n;
    });

    // ── Fill nodes (level_3) — kept PER HUB so each hub's children flow into
    //    their own next-column nodes (HUB Engaged's Filled/Not Filled are
    //    distinct from HUB Not Engaged's). Node id is namespaced `hub__fill`;
    //    the display name stays the raw fill value. Bridge role only. ──
    const hubRank = new Map<string, number>();
    hubs.forEach((h, i) => hubRank.set(h.id, i));

    const fillMap = new Map<string, { hub: string; name: string; n: number }>();
    if (bridgeIdx !== null) {
      data.forEach(r => {
        const hubId = this.toId(r.hub);
        if (!hubId) return;
        const name = r.fill ? r.fill : lbl.notFilledName;
        const id = `${hubId}__${this.toId(name)}`;
        const cur = fillMap.get(id) || { hub: hubId, name, n: 0 };
        cur.n += r.count;
        fillMap.set(id, cur);
      });
    }
    const fillOrder = bridgeIdx !== null ? orderListFor(bridgeIdx) : [];
    const fills: FillNode[] = Array.from(fillMap.entries()).map(([id, info]) => ({
      id, name: info.name, n: info.n, hub: info.hub,
      color: this.getColor(info.name)
    }))
    // Group by hub order (each hub's fills stay together), then
    // valueNodeOrder.level_3 within the hub, then count desc.
    .sort((a, b) => {
      const ha = hubRank.get(a.hub || '') ?? Infinity, hb = hubRank.get(b.hub || '') ?? Infinity;
      if (ha !== hb) return ha - hb;
      const ra = orderRank(fillOrder, a.name), rb = orderRank(fillOrder, b.name);
      if (ra !== rb) return ra - rb;
      return b.n - a.n;
    });

    // ── Lanes (level_4 outcomes) — also PER HUB; id is `hub__outcome` and
    //    fillGroup is the hub-scoped fill id so each outcome stays under its
    //    own hub's fill bar. pct is relative to the grand total. ──
    const laneMap = new Map<string, { hub: string; name: string; n: number; fillGroup: string; fillRaw: string }>();
    if (laneIdx !== null) {
      data.forEach(r => {
        if (!r.outcome) return;
        const hubId = this.toId(r.hub);
        if (!hubId) return;
        const fillRaw   = bridgeIdx !== null ? (r.fill ? r.fill : lbl.notFilledName) : lbl.notFilledName;
        const fillGroup = `${hubId}__${this.toId(fillRaw)}`;
        const id        = `${hubId}__${this.toId(r.outcome)}`;
        const cur = laneMap.get(id) || { hub: hubId, name: r.outcome, n: 0, fillGroup, fillRaw };
        cur.n += r.count;
        laneMap.set(id, cur);
      });
    }
    const hubTotal = total;

    // Cross-fillGroup ordering follows the already-sorted `fills` array (hub
    // order, then valueNodeOrder.level_3). Bridge bars inherit their y-range
    // from their lane group, so this drives the top-to-bottom order in the
    // rendered chart.
    const fillIndex = new Map<string, number>();
    fills.forEach((f, i) => fillIndex.set(f.id, i));
    const fillGroupCount = new Map<string, number>();
    fillMap.forEach((info, id) => fillGroupCount.set(id, info.n));

    const laneOrder = laneIdx !== null ? orderListFor(laneIdx) : [];
    const lanes: Lane[] = Array.from(laneMap.entries()).map(([id, info]) => {
      const color = this.resolveOutcomeColor(info.name, info.fillRaw);
      const bg    = this.resolveOutcomeBg(info.name);
      return { id, name: info.name, n: info.n, pct: Math.round((info.n / hubTotal) * 1000) / 10, color, bg, fillGroup: info.fillGroup, hub: info.hub };
    // Sort: fill column order across groups (hub, then level_3), then
    // valueNodeOrder.level_4 within each group, then count desc.
    }).sort((a, b) => {
      if (a.fillGroup !== b.fillGroup) {
        const ia = fillIndex.has(a.fillGroup) ? (fillIndex.get(a.fillGroup) as number) : Infinity;
        const ib = fillIndex.has(b.fillGroup) ? (fillIndex.get(b.fillGroup) as number) : Infinity;
        if (ia !== ib) return ia - ib;
        return (fillGroupCount.get(b.fillGroup) || 0) - (fillGroupCount.get(a.fillGroup) || 0);
      }
      const ra = orderRank(laneOrder, a.name), rb = orderRank(laneOrder, b.name);
      if (ra !== rb) return ra - rb;
      return b.n - a.n;
    });

    // ── SP → HUB links (every hub seen in data) ──
    const spHubMap = new Map<string, number>();
    data.forEach(r => {
      const hubId = this.toId(r.hub);
      if (!hubId) return;
      const key = `${spId(r.sp)}|${hubId}`;
      spHubMap.set(key, (spHubMap.get(key) || 0) + r.count);
    });
    const sp_hub: Link[] = Array.from(spHubMap.entries())
      .map(([key, v]) => { const [s, t] = key.split('|'); return { s, t, v }; })
      .filter(l => l.v > 0);

    // ── HUB → Fill links — bridge role only. Each hub links to its OWN
    //    (hub-scoped) fill nodes so the two hubs' downstreams never merge.
    //    When bridge is skipped (N=3), emit HUB → Lane links the same way. ──
    const hubFillMap = new Map<string, number>();
    const hubLaneMap = new Map<string, number>();
    if (bridgeIdx !== null) {
      data.forEach(r => {
        const hubId = this.toId(r.hub);
        if (!hubId) return;
        const fillId = `${hubId}__${r.fill ? this.toId(r.fill) : DEFAULT_FG}`;
        const key = `${hubId}|${fillId}`;
        hubFillMap.set(key, (hubFillMap.get(key) || 0) + r.count);
      });
    } else if (laneIdx !== null) {
      data.forEach(r => {
        if (!r.outcome) return;
        const hubId = this.toId(r.hub);
        if (!hubId) return;
        const laneId = `${hubId}__${this.toId(r.outcome)}`;
        const key = `${hubId}|${laneId}`;
        hubLaneMap.set(key, (hubLaneMap.get(key) || 0) + r.count);
      });
    }
    const splitHubLink = (m: Map<string, number>): Link[] =>
      Array.from(m.entries()).map(([key, v]) => {
        const [s, t] = key.split('|');
        return { s, t, v };
      });
    const hub_fill: Link[] = splitHubLink(hubFillMap);
    const hub_lane: Link[] = splitHubLink(hubLaneMap);

    // ── Per-patient dots — only when a lane role exists. lane / fillGroup are
    //    hub-scoped so each dot belongs to exactly one hub's swimlane. ──
    const patients: Patient[] = [];
    if (laneIdx !== null) {
      data.forEach(r => {
        if (r.time === null) return;
        const days = parseFloat(String(r.time));
        if (isNaN(days)) return;
        const hubId = this.toId(r.hub);
        patients.push({
          id: r.patientId,
          sp: spId(r.sp),
          hub: hubId,
          lane: `${hubId}__${this.toId(r.outcome)}`,
          fillGroup: `${hubId}__${r.fill ? this.toId(r.fill) : DEFAULT_FG}`,
          days
        });
      });
    }

    // ── Time distribution → median + p90 + maxDays.
    //    "Median time to fill" only counts rows whose level_3 is "Filled"
    //    (across all hubs), read straight from the data so the hub-scoped
    //    dot ids don't matter here. ──
    const filledTimes: number[] = data
      .filter(r => r.time !== null && this.toId(r.fill) === this.toId('Filled'))
      .map(r => parseFloat(String(r.time)))
      .filter(n => !isNaN(n));
    filledTimes.sort((a, b) => a - b);

    let median = 0;
    if (filledTimes.length > 0) {
      const mid = Math.floor(filledTimes.length / 2);
      median = filledTimes.length % 2 === 1
        ? filledTimes[mid]
        : (filledTimes[mid - 1] + filledTimes[mid]) / 2;
    }

    // p90 drives the chart x-extent. At-risk threshold mirrors the median
    // so the bubble tooltip "At risk" badge agrees with the visible band.
    const times = patients.map(p => p.days).sort((a, b) => a - b);
    const p90     = times.length ? times[Math.floor(times.length * 0.90)] : cfgKnobs.p90DaysFallback;
    const atRisk  = median;
    // The x-axis cap is DATA-DRIVEN: it's the actual largest day in the data
    // (never an arbitrary hard cap). p90 × 3 still compresses extreme tails, but
    // it can never extend past the real maximum, so the axis always ends at the
    // data's true range.
    const dataMaxDay = times.length ? Math.ceil(times[times.length - 1]) : cfgKnobs.p90DaysFallback;
    const maxDays = Math.max(1, Math.min(Math.round(p90 * 3), dataMaxDay));
    const cfg: Cfg = { median, atRisk, maxDays };

    // Surface aggregates the navigation handler needs to reproduce the
    // chart's first-level (top-N) rollup when mapping a clicked node back to
    // raw patient ids. Downstream levels aggregate across all hubs, so no
    // engaged-hub scoping is carried here anymore.
    this.namedSPNames = namedSPNames;
    this.roleLevelIndex = roles;

    return { total, sps, hubs, fills, lanes, sp_hub, hub_fill, hub_lane, cfg, patients };
  }

  private toId(name: string): string {
    return (name || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  /* ────────────────────────────────────────────────── */
  /*  Main render pipeline                              */
  /* ────────────────────────────────────────────────── */

  private render() {
    const el = this.canvasRef.nativeElement;
    d3.select(el).selectAll('*').remove();

    const hasLane   = this.roleLevelIndex.lane   !== null;
    // The day-range zoom brush is shown only when it has a beeswarm to act on
    // AND the config flag is on. When off, no band is reserved and no zoom can
    // be active — the beeswarm always renders at full range.
    const showBrush = hasLane && this.props.config.showDayBrush !== false;
    if (!showBrush) {
      // No brush → no zoom; drop any stale focus window / brush handles so the
      // beeswarm renders at full range and nothing references removed DOM.
      this.dayDomain = null;
      this.dayBrush = null;
      this.dayBrushGroup = null;
      this.dayOverviewScale = null;
    }
    // Grow the viewBox by the day-brush band so the range-selector strip has
    // room below the x-axis. Skipped entirely when the brush is off, so the
    // SVG keeps its original height.
    this.viewBoxH = this.H + (showBrush ? DAY_BRUSH_BAND_H : 0);

    this.svg = d3.select(el)
      .attr('viewBox', `0 0 ${W} ${this.viewBoxH}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    this.computeLayouts();
    const hasBridge = this.roleLevelIndex.bridge !== null;
    // Swimlanes / at-risk band / median guide / x-axis / beeswarm / lasso are
    // all anchored to the LAST column (lane). Skip them entirely when there
    // is no lane role bound (e.g. a 2-level Sankey).
    if (hasLane) {
      this.drawSwimLanes();
      // Placeholder container for the focus-dependent layers (at-risk band /
      // median line / x-axis). Created HERE so it sits behind the dots in
      // z-order; drawBeeswarmDots → applyFocusTransform fills it, and every
      // brush event refills it in place (so the axis/median/band track the
      // zoom smoothly without disturbing z-order).
      this.svg.append('g').attr('class', 'focus-bg');
      if (showBrush) this.drawDayBrush();
    }
    this.drawSankeyLinks();
    this.drawSPNodes();
    this.drawHUBNodes();
    if (hasBridge) this.drawBridgeBars();
    // (Outcome labels are now rendered above each lane stub bar inside drawLaneBars(),
    //  matching SP / HUB / BRIDGE styling.)
    // Lasso overlay is drawn BEFORE the dots so the dots end up on top of the
    // overlay rect — this lets each dot receive its own mouseenter/leave (for
    // tooltips) while the overlay still catches drags that begin in empty space.
    if (hasLane) {
      this.drawLassoOverlay();
      this.drawBeeswarmDots();
      // Lane stub bars + their above-bar labels are drawn LAST so their nav-icon
      // hit areas sit on top of the lasso overlay rect (which spans the whole
      // beeswarm area with pointer-events: all). Without this, the LANE nav
      // chevron's clicks get swallowed by the lasso overlay. The lane stub bars
      // themselves don't overlap dots horizontally, so dots stay hoverable.
      this.drawLaneBars();
    }
  }

  /* ──────────── 1. Compute Layouts ──────────── */
  private computeLayouts() {
    const d = this.chartData;
    // Shrink the inner working area by AXIS_GAP so the lowest lane (and its
    // boosted bar / Abandonment band) lands well above the x-axis baseline.
    const usableH = this.H - MARGIN.top - MARGIN.bottom - AXIS_GAP;
    const totalLaneN = d.lanes.reduce((s, l) => s + l.n, 0);
    // When no lane role is bound (or no lane rows exist) the entire
    // lane / bridge / beeswarm pipeline is skipped — we still want SP and
    // HUB columns to render, so we no longer early-return here.
    const hasLane = this.roleLevelIndex.lane !== null && totalLaneN > 0;

    // Lane gaps must clear the boosted lane bars + the 2-line label that sits
    // above each lane bar (~32px tall). Formula: gap >= label_h + boost_up +
    // boost_down + buffer  ≈  32 + 14 + 6 + 4  =  56. BETWEEN_GAP adds extra
    // breathing room between fill groups.
    const BETWEEN_GAP = LANE_BETWEEN_GAP;
    const WITHIN_GAP  = LANE_WITHIN_GAP;
    const HUB_GAP     = 44;  // gap between hub bars — fits a 2-line label above the lower hub
    // Per-column height steps. Bigger inset = shorter bars. SP and HUB share
    // the same inset so they read as one tier on the left; bridge bars step
    // up from there, and the boosted lane bar is tallest on the right.
    const NODE_INSET_SP  = 30;  // SP — same height as HUB
    const NODE_INSET_HUB = 30;  // HUB — same height as SP
    // SP (level 1) and HUB are sized against the BASE height, not the grown
    // one, so they stay compact when the beeswarm/level-4 side expands. The
    // base-height band is centered vertically within the full (grown) area so
    // the ribbons fan out symmetrically toward the taller right-hand columns.
    const baseUsableH = Math.min(usableH, this.baseH - MARGIN.top - MARGIN.bottom - AXIS_GAP);
    const leftBandY   = MARGIN.top + Math.max(0, (usableH - baseUsableH) / 2);
    // SP node area (used by SP layout below).
    const spAreaY  = leftBandY + NODE_INSET_SP;
    const spAreaH  = Math.max(0, baseUsableH - 2 * NODE_INSET_SP);
    // HUB node area (used by HUB layout below).
    const hubAreaY = leftBandY + NODE_INSET_HUB;
    const hubAreaH = Math.max(0, baseUsableH - 2 * NODE_INSET_HUB);

    // ── 1. Hub nodes — heights are strictly proportional to each hub's count.
    //    Order comes entirely from `d.hubs` (set in buildChartData:
    //    `valueNodeOrder.level_2` wins, then engaged-hub-first, then count
    //    desc), so we render in that order verbatim. ──
    const totalHUBN     = d.hubs.reduce((s, h) => s + h.n, 0) || 1;

    const hubBars = [...d.hubs];
    const hubGapsTotal = Math.max(0, hubBars.length - 1) * HUB_GAP;
    const hubBudget    = Math.max(0, hubAreaH - hubGapsTotal);

    this.hubNodes = [];
    let hubY = hubAreaY;
    hubBars.forEach((h, i) => {
      const barH = Math.round((h.n / totalHUBN) * hubBudget);
      if (barH <= 0) return;
      this.hubNodes.push({ id: h.id, y: hubY, h: barH, color: h.color,
        name: h.name, n: h.n, pct: Math.round((h.n / d.total) * 100) });
      hubY += barH + (i < hubBars.length - 1 ? HUB_GAP : 0);
    });

    // ── 2. Lanes — constrained to Hub Engaged bar height. Skipped wholesale
    //    when no lane role is bound; SP/HUB still render below. ──
    let laneBudget = 0;
    // When the canvas is capped (see MAX_CANVAS_H) the full gaps may not fit;
    // scale them (and the lane-bar boost below) down so the bars still get a
    // positive budget and nothing overlaps. 1 = no compression.
    let laneGapScale = 1;
    if (hasLane) {
      const nGaps = d.lanes.reduce((sum, l, i) => {
        if (i === 0) return sum;
        return sum + (d.lanes[i - 1].fillGroup === l.fillGroup ? WITHIN_GAP : BETWEEN_GAP);
      }, 0);
      const minBarsTotal = d.lanes.length * MIN_NODE_H;
      if (nGaps > 0 && nGaps + minBarsTotal > usableH) {
        laneGapScale = Math.max(0.2, (usableH - minBarsTotal) / nGaps);
      }
      laneBudget = Math.max(minBarsTotal, usableH - nGaps * laneGapScale);

      // Give each lane MIN_NODE_H, then share the remaining budget by value so
      // a 1-patient lane is still a visible bar while big lanes stay taller.
      const pool = Math.max(0, laneBudget - minBarsTotal);
      let y = MARGIN.top;
      this.laneRects = d.lanes.map((l, i) => {
        const h = MIN_NODE_H + (l.n / totalLaneN) * pool;
        const rect = { id: l.id, y, h };
        const nextGap = (i < d.lanes.length - 1
          ? (l.fillGroup === d.lanes[i + 1].fillGroup ? WITHIN_GAP : BETWEEN_GAP) : 0) * laneGapScale;
        y += h + nextGap;
        return rect;
      });
    } else {
      this.laneRects = [];
    }

    // ── 3. SP nodes — proportional to TOTAL SP patients (hub_eng + hub_not_eng) ──
    // Gap between SP bars must fit a 2-line label (pct + count, then name) above each lower bar.
    const SP_GAP = 44;
    const totalSP = d.sps.reduce((s, n) => s + n.n, 0) || 1;
    const spGapsTotal = Math.max(0, d.sps.length - 1) * SP_GAP;
    const spBudget = Math.max(0, spAreaH - spGapsTotal);
    let spY = spAreaY;
    this.spNodes = d.sps.map((sp, i) => {
      const h = (sp.n / totalSP) * spBudget;
      const node: NodePos = { id: sp.id, y: spY, h, color: sp.color, name: sp.name, n: sp.n,
        pct: Math.round((sp.n / d.total) * 100) };
      spY += h + (i < d.sps.length - 1 ? SP_GAP : 0);
      return node;
    });

    // ── 4. Bridge bars — span their lane group's y-range. Skipped entirely
    //    when the bridge role is unbound (e.g. levels=['level_1','level_2','level_4']).
    //    `d.fills` is empty in that case so the .map() degenerates to []
    //    naturally, but we keep the guard explicit for readability. ──
    const fillGroups: Record<string, { lanes: Lane[]; rects: LaneRect[] }> = {};
    d.lanes.forEach((l, i) => {
      if (!fillGroups[l.fillGroup]) fillGroups[l.fillGroup] = { lanes: [], rects: [] };
      fillGroups[l.fillGroup].lanes.push(l);
      fillGroups[l.fillGroup].rects.push(this.laneRects[i]);
    });

    const hubN = d.total || totalLaneN || 1;
    // Bridge (fill) bar height is strictly proportional to the fill's own
    // count, using the same px-per-patient scale as the outcome column, and
    // centered on its lane group's y-range so the ribbons in/out stay balanced.
    const laneScale = (hasLane && totalLaneN > 0) ? laneBudget / totalLaneN : 0;
    this.bridgeBars = this.roleLevelIndex.bridge === null ? [] : d.fills.map(f => {
      const group = fillGroups[f.id];
      if (!group) return { id: f.id, y: 0, h: 0, color: f.color, name: f.name, n: f.n,
        pct: Math.round((f.n / hubN) * 100), hub: f.hub };
      const groupTop = Math.min(...group.rects.map(r => r.y));
      const groupBot = Math.max(...group.rects.map(r => r.y + r.h));
      const groupSpan = Math.max(MIN_NODE_H, groupBot - groupTop);
      const groupMid = (groupTop + groupBot) / 2;
      // Value-proportional, but never taller than its own outcome group's span,
      // so a fill bar can't bleed into the adjacent group's bar (groups are
      // separated by the lane gaps) — i.e. always keeps space between nodes.
      const h = Math.min(groupSpan, Math.max(MIN_NODE_H, f.n * laneScale));
      return { id: f.id, y: groupMid - h / 2, h, color: f.color, name: f.name, n: f.n,
        pct: Math.round((f.n / hubN) * 100), hub: f.hub };
    });

    // ── 5. Lane stub bars (one per outcome, sit just before the swim lane) ──
    // Height is strictly proportional to the outcome's value — it equals the
    // proportional lane rect with NO fixed boost — so a small outcome reads
    // small and a large one reads large. The 2px floor just keeps a near-zero
    // lane visible.
    this.laneBars = !hasLane ? [] : d.lanes.map((lane, i) => {
      const rect = this.laneRects[i];
      return { id: lane.id,
        y: rect.y,
        h: rect.h, // already includes the MIN_NODE_H floor (see laneRects)
        color: lane.color,
        name: lane.name, n: lane.n, pct: lane.pct, hub: lane.hub };
    });

    // ── 5b. Zero-day band width ── The "≤ 0 day" zone holds every lane's
    // 0/negative dots, packed from the node rightward. Its width is the ACTUAL
    // rightmost extent of those dots (simulated the same way the beeswarm packs
    // them), so the 0d tick lands exactly where the zero cluster ends.
    this.zeroBandWidth = this.computeZeroBandWidth(d, hasLane);

    // ── 6. Sankey links ── (ribbons inherit the TARGET node's colour so each
    //     branch carries the colour of where it's flowing INTO — matches design.)
    this.spHubLinks = this.computeSankeyLinks(d.sp_hub, this.spNodes, this.hubNodes, COL_SP + BAR_W, COL_HUB, false);

    if (this.roleLevelIndex.bridge !== null) {
      this.hubFillLinks = this.computeSankeyLinks(d.hub_fill, this.hubNodes, this.bridgeBars, COL_HUB + BAR_W, COL_FILL, false);

      // FILL (bridge) → LANE (per-outcome). Source side packs each lane's slice
      // TIGHTLY inside the parent bridge bar (no gaps between lanes within the
      // same fill group), proportional to the lane's count. Target side hits
      // the boosted lane stub bar — which has gaps between lanes and a height
      // boost. The y + height mismatch between the tight source stack and the
      // gapped target makes each ribbon trace a clear S-curve into its lane.
      const groupTotals: Record<string, number> = {};
      d.lanes.forEach(l => { groupTotals[l.fillGroup] = (groupTotals[l.fillGroup] || 0) + l.n; });
      const groupSrcOffsets: Record<string, number> = {};
      this.fillLaneLinks = d.lanes.map((lane, i) => {
        const bar    = this.laneBars[i];
        const bridge = this.bridgeBars.find(b => b.id === lane.fillGroup);
        const bridgeY = bridge ? bridge.y : bar.y;
        const bridgeH = bridge ? bridge.h : bar.h;
        const groupTotal = groupTotals[lane.fillGroup] || lane.n || 1;
        const sourceH = (lane.n / groupTotal) * bridgeH;
        const sourceY = bridgeY + (groupSrcOffsets[lane.fillGroup] || 0);
        groupSrcOffsets[lane.fillGroup] = (groupSrcOffsets[lane.fillGroup] || 0) + sourceH;
        return {
          s: lane.fillGroup, t: lane.id, v: lane.n, color: lane.color,
          x0: COL_FILL + BAR_W, y0: sourceY, h0: sourceH,
          x1: COL_LANE,         y1: bar.y,   h1: bar.h
        };
      });
      this.hubLaneLinks = [];
    } else {
      // No bridge column (N=3): chain every HUB bar straight into the lane
      // stubs. computeSankeyLinks distributes each hub's outflow across the
      // lanes and each lane's inflow across the hubs, so ALL hubs feed the
      // shared lanes (not just one engaged hub).
      this.hubFillLinks  = [];
      this.fillLaneLinks = [];
      this.hubLaneLinks  = this.computeSankeyLinks(
        d.hub_lane || [], this.hubNodes, this.laneBars, COL_HUB + BAR_W, COL_LANE, false
      );
    }
  }

  private computeSankeyLinks(links: Link[], sources: NodePos[], targets: NodePos[], x0: number, x1: number, useSourceColor: boolean): SankeyLink[] {
    const result: SankeyLink[] = [];
    const srcOffsets: Record<string, number> = {};
    const tgtOffsets: Record<string, number> = {};
    sources.forEach(s => srcOffsets[s.id] = 0);
    targets.forEach(t => tgtOffsets[t.id] = 0);

    for (const link of links) {
      const src = sources.find(s => s.id === link.s);
      const tgt = targets.find(t => t.id === link.t);
      if (!src || !tgt) continue;

      const srcTotal = links.filter(l => l.s === link.s).reduce((a, l) => a + l.v, 0);
      const tgtTotal = links.filter(l => l.t === link.t).reduce((a, l) => a + l.v, 0);

      const h0 = (link.v / srcTotal) * src.h;
      const h1 = (link.v / tgtTotal) * tgt.h;
      const y0 = src.y + (srcOffsets[link.s] || 0);
      const y1 = tgt.y + (tgtOffsets[link.t] || 0);

      srcOffsets[link.s] = (srcOffsets[link.s] || 0) + h0;
      tgtOffsets[link.t] = (tgtOffsets[link.t] || 0) + h1;

      result.push({
        s: link.s, t: link.t, v: link.v,
        color: useSourceColor ? src.color : tgt.color,
        x0, y0, h0, x1, y1, h1
      });
    }
    return result;
  }

  /** Width of the ≤0-day band = the actual rightmost extent of the left-
   *  anchored zero/negative dots. Simulated exactly like the beeswarm packs
   *  them (same anchor, forces, collide radius and clamps) so the 0d tick lands
   *  precisely where the zero cluster ends — no gap, no overlap. Capped so the
   *  band can't eat more than 45% of the beeswarm width. */
  private computeZeroBandWidth(d: SankeyBeeswarmData, hasLane: boolean): number {
    if (!hasLane || !Array.isArray(d.patients) || !d.patients.length) return 0;
    const anchorX = DOT_X_START + DOT_R;
    let maxRight = DOT_X_START;
    d.lanes.forEach((lane, li) => {
      const bar = this.laneBars[li];
      if (!bar) return;
      const zeros = d.patients!.filter(p => p.lane === lane.id && p.days <= 0);
      if (!zeros.length) return;
      const cy = bar.y + bar.h / 2;
      const yMin = bar.y + DOT_R;
      const yMax = Math.max(yMin, bar.y + bar.h - DOT_R);
      const nodes: Array<{ x: number; y: number }> = zeros.map(() => ({ x: anchorX, y: cy }));
      const sim = d3.forceSimulation(nodes as any)
        .force('x', d3.forceX(anchorX).strength(1))
        .force('y', d3.forceY(cy).strength(0.3))
        .force('collide', d3.forceCollide(DOT_R + 2.2))
        .stop();
      for (let k = 0; k < 120; k++) {
        sim.tick();
        for (const n of nodes) {
          if (n.x < DOT_X_START) n.x = DOT_X_START;
          if (n.y < yMin) n.y = yMin; else if (n.y > yMax) n.y = yMax;
        }
      }
      const right = Math.max(...nodes.map(n => n.x)) + DOT_R;
      if (right > maxRight) maxRight = right;
    });
    const maxBand = (BEE_RIGHT - DOT_X_START) * 0.45;
    return Math.min(maxBand, Math.max(0, maxRight - DOT_X_START));
  }

  /** Shared beeswarm x-scale: positive days map from the 0d tick
   *  (DOT_X_START + zeroBandWidth) to BEE_RIGHT. The ≤0-day band sits to the
   *  LEFT of the tick (in [DOT_X_START, 0d tick]) and is handled separately in
   *  drawBeeswarmDots. Used by the axis, median line, at-risk band and dots so
   *  they all stay in sync. */
  private beeXScale(): d3.ScaleLinear<number, number> {
    const [d0, d1] = this.focusDomain();
    return d3.scaleLinear()
      .domain([d0, d1])
      .range([DOT_X_START + this.effZeroBand(), BEE_RIGHT]);
  }

  /** Current beeswarm day window: the brushed focus window, or the full
   *  [0, maxDays] range when no zoom is active. */
  private focusDomain(): [number, number] {
    const maxDays = this.chartData?.cfg?.maxDays || 1;
    return this.dayDomain ?? [0, maxDays];
  }

  /** The FULL-range day scale, independent of any zoom. The dots are laid out
   *  once against this; the focus zoom is applied later as a viewport remap. */
  private fullBeeXScale(): d3.ScaleLinear<number, number> {
    const maxDays = this.chartData?.cfg?.maxDays || 1;
    return d3.scaleLinear()
      .domain([0, maxDays])
      .range([DOT_X_START + this.zeroBandWidth, BEE_RIGHT]);
  }

  /** The ≤0-day band only makes sense at full zoom (it holds the zero/negative
   *  cluster to the LEFT of the 0d tick). When zoomed into a positive window
   *  those dots aren't shown, so the band collapses to 0. */
  private effZeroBand(): number {
    return (this.dayDomain && this.dayDomain[0] > 0) ? 0 : this.zeroBandWidth;
  }

  /* ──────────── 2. Draw Swim Lanes ──────────── */
  private drawSwimLanes() {
    const self = this;
    const g = this.svg.append('g').attr('class', 'swim-lanes');
    const d = this.chartData;

    d.lanes.forEach((lane, i) => {
      // Anchor swimlane bg + border to the boosted lane bar (NOT the
      // proportional rect) so the dot area, the lane stub bar, and the
      // outcome label all share the same y span on the last column.
      const bar = this.laneBars[i];
      g.append('rect')
        .attr('class', `lane lane-${lane.id}`)
        .attr('x', BEE_LEFT)
        .attr('y', bar.y)
        .attr('width', BEE_RIGHT - BEE_LEFT)
        .attr('height', bar.h)
        .attr('fill', lane.bg)
        .attr('rx', 4)
        .style('opacity', 0.45)
        .style('transition', 'opacity .2s');

      // Dashed border
      g.append('rect')
        .attr('x', BEE_LEFT)
        .attr('y', bar.y)
        .attr('width', BEE_RIGHT - BEE_LEFT)
        .attr('height', bar.h)
        .attr('fill', 'none')
        .attr('stroke', lane.color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3')
        .attr('rx', 4)
        .style('opacity', 0.4);
    });
  }

  /** Predicate used to identify at-risk / Abandonment lanes. Honours
   *  `props.config.atRiskMode`:
   *    'all'      — every lane qualifies (band paints across the whole
   *                 beeswarm past the median).
   *    'specific' — match against `atRiskLaneId` (comma-separated list of
   *                 ids) or `atRiskLaneNamePattern` (comma-separated regex
   *                 patterns tested against `lane.name`). A lane qualifies
   *                 if ANY id OR ANY pattern hits, so a future multi-lane
   *                 config is a config edit, not a code change. */
  private matchesAtRiskLane(lane: { id: string; name: string }): boolean {
    const cfg = this.props.config;
    if (cfg.atRiskMode === 'all') return true;

    const ids = this.parseList(cfg.atRiskLaneId);
    if (ids.includes(lane.id)) return true;

    const patterns = this.parseList(cfg.atRiskLaneNamePattern);
    for (const p of patterns) {
      try {
        if (new RegExp(p, 'i').test(lane.name)) return true;
      } catch { /* invalid pattern → skip */ }
    }
    return false;
  }

  /** Split a comma-separated config value into a clean string[]. Accepts
   *  arrays too so a future config can pass either form. Empty / whitespace
   *  entries are dropped. */
  private parseList(v: string | string[] | undefined | null): string[] {
    if (v == null) return [];
    const arr = Array.isArray(v) ? v : String(v).split(',');
    return arr.map(s => String(s).trim()).filter(Boolean);
  }

  /** Set of lane ids matching the at-risk predicate. Used by the bubble
   *  tooltip badge + lasso "at risk" count so a multi-lane config rolls up
   *  consistently with the visual band. */
  private getAtRiskLaneIds(): Set<string> {
    const out = new Set<string>();
    if (!this.chartData) return out;
    this.chartData.lanes.forEach(l => {
      if (this.matchesAtRiskLane(l)) out.add(l.id);
    });
    return out;
  }

  /* ──────────── 3. At Risk Zone ──────────── */
  private drawAtRiskZone() {
    const cfg = this.chartData.cfg;
    // Collect every lane that qualifies. With `atRiskMode === 'all'` this is
    // the full set; with 'specific' it's only the ids/patterns listed in
    // `props.config`. Empty → nothing to draw.
    const rects = this.chartData.lanes
      .map((l, i) => this.matchesAtRiskLane(l) ? this.laneBars[i] : null)
      .filter((r): r is NodePos => !!r);
    if (rects.length === 0) return;

    // Wrap in a group so a focus re-render (zoom) can drop + redraw the band
    // cleanly without leaving the caption text behind. Drawn into focusBgTarget
    // (behind the dots) when a zoom redraw is in progress.
    const group = (this.focusBgTarget || this.svg).append('g').attr('class', 'at-risk-group');

    const xScale = this.beeXScale();
    const [d0, d1] = this.focusDomain();
    // The band starts at the median (everything past it is at risk). When the
    // focus window is entirely BELOW the median there's nothing at risk to
    // paint; otherwise clamp the band's left edge into the visible range.
    if (cfg.median > d1) return;
    // Band left edge:
    //   • median inside the window  → start at the median tick (xScale(median)).
    //   • whole window already past the median (median ≤ d0, e.g. after brushing
    //     right) → everything visible is at risk, so extend the wash all the way
    //     to the node bars' edge (BEE_LEFT) — matching the swimlane background —
    //     instead of stopping at the beeswarm's inner left edge.
    const atRiskX = cfg.median <= d0 ? BEE_LEFT : xScale(cfg.median);

    // Extend the band a bit further beyond each lane's edges so the red
    // wash reads clearly. The "At risk" label sits above the topmost band
    // with a few px of breathing room before the rectangle starts.
    const bandTopInset    = 8;
    const bandBottomInset = 10;
    const labelGapAboveBand = 6;

    rects.forEach(rect => {
      group.append('rect')
        .attr('class', 'at-risk-zone')
        .attr('x', atRiskX)
        .attr('y', rect.y - bandTopInset)
        .attr('width', BEE_RIGHT - atRiskX)
        .attr('height', rect.h + bandTopInset + bandBottomInset)
        .attr('fill', this.props.colors.atRiskBand)
        .attr('opacity', 0.3)
        .attr('rx', 4);
    });

    // Single caption above the TOPMOST qualifying band — stacking multiple
    // "At Risk of Abandonment" labels across lanes would be visually noisy
    // and redundant.
    const topRect = rects.reduce((a, b) => (a.y <= b.y ? a : b));
    const atRiskMidX = (atRiskX + BEE_RIGHT) / 2;
    group.append('text')
      .attr('x', atRiskMidX)
      .attr('y', topRect.y - bandTopInset - labelGapAboveBand)
      .attr('text-anchor', 'middle')
      .text(this.props.labels.atRiskBand)
      .attr('fill', this.props.colors.atRiskText)
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .attr('font-family', 'Poppins, sans-serif');
  }

  /* ──────────── 4. Median Line ────────────
   * Median line is a global reference — it spans the full chart height across
   * every lane, not just Pending. (Only the at-risk band/label is Pending-only.)
   */
  private drawMedianLine() {
    const cfg = this.chartData.cfg;
    const [d0, d1] = this.focusDomain();
    // The median guide is only meaningful when it falls inside the visible
    // window — when zoomed past it, skip the line entirely.
    if (cfg.median < d0 || cfg.median > d1) return;
    const xScale = this.beeXScale();
    const mx = xScale(cfg.median);

    // Push the line + label well above the lane stub bar's 2-line above-
    // bar labels. The topmost lane bar starts at MARGIN.top − BOOST_UP and
    // its pct/name label cap-top sits roughly at MARGIN.top − BOOST_UP − 28,
    // so we extend the median line above that by another ~20px and place
    // the caption above the line top.
    const lineTopY  = MARGIN.top - 50;
    const labelY    = MARGIN.top - 56;

    // Wrap so a focus re-render (zoom) can drop + redraw cleanly.
    const group = (this.focusBgTarget || this.svg).append('g').attr('class', 'median-group');

    group.append('line')
      .attr('class', 'median-line')
      .attr('x1', mx).attr('x2', mx)
      .attr('y1', lineTopY)
      .attr('y2', this.H - MARGIN.bottom)
      .attr('stroke', this.props.colors.medianLine)
      .attr('stroke-width', 1.2)
      .attr('stroke-dasharray', '7,5');

    group.append('text')
      .attr('x', mx)
      .attr('y', labelY)
      .attr('text-anchor', 'middle')
      .text(this.props.labels.medianLine)
      .attr('fill', this.props.colors.medianText)
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .attr('fill', '#101D42')
      .attr('font-family', 'Poppins, sans-serif');
  }

  /* ──────────── 5. X Axis ──────────── */
  private drawXAxis() {
    const cfg = this.chartData.cfg;
    const xScale = this.beeXScale();
    const g = (this.focusBgTarget || this.svg).append('g').attr('class', 'x-axis');

    // Colour + label refs pulled once up front; the loop below reuses them.
    const lbl  = this.props.labels;
    const clr  = this.props.colors;

    // Axis baseline starts flush at the lane stub bar (BEE_LEFT) so the scale
    // visually anchors to the bar. Dots/ticks still start at DOT_X_START
    // (= BEE_LEFT + DOT_R + 4) so the leftmost bubble doesn't overlap the bar.
    g.append('line')
      .attr('x1', BEE_LEFT).attr('x2', BEE_RIGHT)
      .attr('y1', this.H - MARGIN.bottom + 4).attr('y2', this.H - MARGIN.bottom + 4)
      .attr('stroke', clr.axisLine).attr('stroke-width', 1).attr('fill', '#8A98AB');

    // Axis caption — centered on the scale midpoint, not anchored to the left.
    g.append('text')
      .attr('x', (BEE_LEFT + BEE_RIGHT) / 2)
      .attr('y', this.H - MARGIN.bottom + 40)
      .attr('text-anchor', 'middle')
      .attr('fill', clr.axisCaption)
      .attr('font-size', 10)
      .attr('font-family', 'Poppins')
      // .attr('font-weight', 600)
      .attr('fill', '#8A98AB')
      .text(lbl.xAxisCaption);

    // Generate clean ticks across the FOCUS window (full range when not
    // zoomed), then add the median tick if it falls inside the window.
    const [d0, d1] = this.focusDomain();
    const niceTicks: number[] = d3.ticks(d0, d1, 7);
    if (cfg.median >= d0 && cfg.median <= d1 && !niceTicks.includes(cfg.median)) {
      niceTicks.push(cfg.median);
    }
    niceTicks.sort((a, b) => a - b);

    niceTicks.forEach(t => {
      const x = xScale(t);
      const isMedian = t === cfg.median;

      g.append('line')
        .attr('x1', x).attr('x2', x)
        .attr('y1', this.H - MARGIN.bottom + 4)
        .attr('y2', this.H - MARGIN.bottom + 10)
        .attr('stroke', isMedian ? clr.axisTickStrong : clr.axisLine);

      if (isMedian) {
        // Highlighted pill for median — placed ABOVE the axis baseline, in the
        // gap between the beeswarm and the tick labels (not below the axis), so
        // the "{n} days" badge reads against the median dashed line at the
        // bottom of the swarm. Bottom edge sits ~2px above the axis line.
        const pillH = 15;
        const pillY = this.H - MARGIN.bottom + 2 - pillH;
        g.append('rect')
          .attr('x', x - 22).attr('y', pillY)
          .attr('width', 44).attr('height', pillH)
          .attr('fill', clr.medianPillFill).attr('rx', 3);
        g.append('text')
          .attr('x', x).attr('y', pillY + 11)
          .attr('text-anchor', 'middle')
          .text(`${t} ${lbl.daysSuffix}`)
          .attr('fill', clr.medianPillText).attr('font-size', 10).attr('font-weight', 700)
          .attr('font-family', 'Poppins, sans-serif');
      } else {
        g.append('text')
          .attr('x', x).attr('y', this.H - MARGIN.bottom + 22)
          .attr('text-anchor', 'middle')
          .text(`${t} ${lbl.daysShortSuffix}`)
          .attr('fill', clr.axisTickText).attr('font-size', 10)
          .attr('font-family', 'Poppins').attr('fill', '#8A98AB');
      }
    });
  }

  /* ──────────── 5b. Day-range Context Brush (focus + context) ────────────
   * Horizontal range selector BELOW the x-axis, modelled on the
   * d3-basic-line-popup brush. The track itself is intentionally EMPTY — no
   * histogram / ticks / caption — only the grey selection rect, handle-lines
   * and white rounded handles. It maps through the SAME day x-scale as the
   * main axis (the full-range beeXScale), so dragging / resizing rescales the
   * beeswarm (the FOCUS) to that day window and re-lays-out the dots.
   */
  private drawDayBrush() {
    const self = this;
    const cfg = this.chartData.cfg;

    // Strip geometry. The track sits in the band the viewBox was grown by,
    // just under the x-axis caption.
    const trackTop = this.H - MARGIN.bottom + 40 + DAY_BRUSH_GAP + 8;
    // Align the brush to the EXISTING day x-scale: same range as the full
    // (un-zoomed) beeXScale, so the strip lines up 1:1 with the axis above.
    const left  = DOT_X_START + this.zeroBandWidth;
    const right = BEE_RIGHT;
    this.dayTrackTop = trackTop;

    // Fixed CONTEXT scale — the full [0, maxDays] day range (=== beeXScale when
    // not zoomed). Brushed pixels are inverted through this to a day window.
    const overview = d3.scaleLinear().domain([0, cfg.maxDays]).range([left, right]);
    this.dayOverviewScale = overview;

    const g = this.svg.append('g').attr('class', 'day-brush');

    // Drop-shadow filter for the white brush handles (mirrors the popup's
    // `d3blp-drop-shadow`).
    const defs = g.append('defs');
    const filter: any = defs.append('filter').attr('id', 'dayBrushShadow')
      .attr('width', 10).attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 1).attr('dy', 1).attr('result', 'offsetBlur');
    filter.append('feFlood').attr('in', 'offsetBlur').attr('flood-color', '#000000')
      .attr('flood-opacity', 0.4).attr('result', 'offsetColor');
    filter.append('feComposite').attr('in', 'offsetColor').attr('in2', 'offsetBlur')
      .attr('operator', 'in').attr('result', 'offsetBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Full-range track background — a tinted strip spanning the WHOLE day range
    // so the full context stays visible behind the (smaller) grey selection
    // rect. Appended BEFORE the brush group so it sits behind the selection +
    // handles. The selection reads as a darker band over this light track.
    g.append('rect').attr('class', 'day-brush-track-bg')
      .attr('x', left).attr('y', trackTop)
      .attr('width', right - left).attr('height', DAY_BRUSH_H)
      .attr('fill', '#EEF1F6').attr('fill-opacity', 1)
      .style('stroke', '#DCE0E6').style('stroke-width', 1)
      .attr('rx', 4);

    // The brush itself — extent confined to the track rectangle.
    const brush = d3.brushX()
      .handleSize(10)
      .extent([[left, trackTop], [right, trackTop + DAY_BRUSH_H]])
      .on('start brush end', (event: any) => self.onDayBrush(event));

    const bg = g.append('g').attr('class', 'day-brush-sel').call(brush as any);
    // Selected-window rect — a blue accent (matches the lasso) so it clearly
    // stands out against the light-grey full-range track behind it, instead of
    // blending in as another grey band.
    bg.selectAll('.selection')
      .attr('fill', '#2196F3').attr('fill-opacity', 0.28)
      .style('stroke', '#2196F3').style('stroke-width', 1.5);

    this.dayBrush = brush;
    this.dayBrushGroup = bg;

    // Decorative handle grip lines (2 per handle). Appended AFTER the brush
    // group so they paint ON TOP of the white handle rects (same as the popup,
    // where the handle lines are appended after the brushGroup). Identical for
    // both handles. Repositioned in positionBrushHandles().
    ['day-handle-w1', 'day-handle-w2', 'day-handle-e1', 'day-handle-e2'].forEach(cls => {
      g.append('line').attr('class', cls)
        .attr('stroke', '#8A98AB').attr('stroke-width', 1);
    });

    // Initialise the window. If a zoom is already active (e.g. surviving a
    // re-render) keep it. Otherwise DEFAULT to the minimum-width window anchored
    // at the LEFT (day 0) — the chart starts zoomed into the lowest day window
    // and the user drags right to explore. We set this.dayDomain here (not just
    // the brush selection) so the beeswarm — laid out later in drawBeeswarmDots
    // → applyFocusTransform — reflects the same default window.
    if (!this.dayDomain) {
      // Size the default window to hold the lowest-day DAY_BRUSH_DEFAULT_POINTS
      // patients: sort the days ascending and take the Nth day as the upper
      // bound. ≤ N points → full range; no points → fall back to the min width.
      const days = (this.chartData.patients ?? [])
        .map(p => p.days)
        .filter(v => typeof v === 'number' && !isNaN(v))
        .sort((a, b) => a - b);
      let d1: number;
      if (days.length === 0) {
        d1 = overview.invert(Math.min(right, left + DAY_BRUSH_MIN_PX));
      } else if (days.length <= DAY_BRUSH_DEFAULT_POINTS) {
        d1 = cfg.maxDays;
      } else {
        d1 = days[DAY_BRUSH_DEFAULT_POINTS - 1];
      }
      // Map back to pixels, clamp into the track, and honour the min width.
      const x1 = Math.max(left + DAY_BRUSH_MIN_PX, Math.min(right, overview(d1)));
      this.dayDomain = [overview.invert(left), overview.invert(x1)];
    }
    const init: [number, number] = [overview(this.dayDomain[0]), overview(this.dayDomain[1])];
    this.suppressDayBrushEvent = true;
    bg.call(brush.move as any, init);
    this.suppressDayBrushEvent = false;
    this.positionBrushHandles();
  }

  /** Brush event handler. Inverts the swept pixel window to a day range, sets
   *  the focus, and applies it as a live viewport remap (applyFocusTransform)
   *  on EVERY event — so the x-axis and data move smoothly as the user drags. */
  private onDayBrush(event: any) {
    if (this.suppressDayBrushEvent) return;
    const scale = this.dayOverviewScale;
    if (!scale) return;
    const [left, right] = scale.range();

    // A new brush gesture invalidates any lasso (the dots are about to move).
    if (event.type === 'start' && this.lassoSelected.size > 0) this.clearLassoOnly();

    const sel = event.selection as [number, number] | null;

    // Cleared (a plain click on the strip) → snap back to the full window.
    if (!sel) {
      this.dayDomain = null;
      this.positionBrushHandles();
      this.applyFocusTransform();
      return;
    }

    let [x0, x1] = sel;
    x0 = Math.max(left, x0);
    x1 = Math.min(right, x1);

    // Enforce a minimum window so the focus can't collapse to a sliver.
    const MIN_WIDTH_PX = DAY_BRUSH_MIN_PX;
    if (x1 - x0 < MIN_WIDTH_PX) {
      x1 = Math.min(right, x0 + MIN_WIDTH_PX);
      if (x1 - x0 < MIN_WIDTH_PX) x0 = Math.max(left, x1 - MIN_WIDTH_PX);
      this.suppressDayBrushEvent = true;
      this.dayBrushGroup.call(this.dayBrush.move, [x0, x1]);
      this.suppressDayBrushEvent = false;
    }

    // A window spanning (nearly) the whole track === full range / no zoom.
    const full = (x1 - x0) >= (right - left) - 0.5;
    const d0 = scale.invert(x0);
    const d1 = scale.invert(x1);
    this.dayDomain = full ? null : [d0, d1];

    this.positionBrushHandles();
    // Live on every brush event → the axis + dots pan/zoom smoothly as you drag.
    this.applyFocusTransform();
  }

  /** Apply the current focus window as a cheap viewport remap (no force-sim
   *  re-layout, so it's smooth on every brush event):
   *   • redraws the focus-dependent background layers (at-risk band / median
   *     line / x-axis) in place, behind the dots;
   *   • remaps each dot's display x from its full-range anchor (baseX) through
   *     the focus scale — baseY is fixed. Dots that fall outside the window map
   *     past the beeswarm edges and are hidden by the clip on the dots group,
   *     so the swarm pans/zooms smoothly. */
  private applyFocusTransform() {
    if (!this.svg || !this.chartData) return;

    // Redraw the focus background (behind the dots) in its stable container.
    let bg = this.svg.select('.focus-bg');
    if (bg.empty()) bg = this.svg.append('g').attr('class', 'focus-bg');
    bg.selectAll('*').remove();
    if (this.roleLevelIndex.lane !== null) {
      this.focusBgTarget = bg;
      this.drawAtRiskZone();
      this.drawMedianLine();
      this.drawXAxis();
      this.focusBgTarget = null;
    }

    // Remap the dots through the focus scale.
    //
    // Re-anchor each POSITIVE dot to its TRUE day on the current focus scale and
    // add back only its swarm collision offset (kept constant in px). Do NOT
    // round-trip baseX through full.invert(): baseX is the post-simulation
    // position, so it already carries the collision drift, and re-projecting it
    // through the focus scale magnifies that drift under zoom — which is what
    // flung low-day bubbles to the wrong side of the axis (e.g. a 8-day dot
    // landing past the 50d tick). With this, a "day N" bubble always sits under
    // the N tick at any zoom; at full range full===focus so x === baseX (no-op).
    const full = this.fullBeeXScale();
    const focus = this.beeXScale();
    // The ≤0-day band only exists while the window still includes 0 (full range
    // or a window whose lower bound is 0). When it does, the zero cluster stays
    // pinned to its base position so it doesn't slide as the user zooms; once
    // the window starts above 0 the band is gone, so those dots are pushed off
    // the left edge (the beeswarm clip hides them) rather than dragged inward.
    const bandActive = this.effZeroBand() > 0;
    this.svg.selectAll('.dot')
      .attr('cx', (dd: Dot) => {
        const baseX = dd.baseX ?? dd.x;
        let x: number;
        if (dd.originalDays <= 0) {
          x = bandActive ? baseX : (DOT_X_START - DOT_R * 4);
        } else {
          const driftPx = baseX - full(dd.days);   // swarm collision offset (px)
          x = focus(dd.days) + driftPx;
        }
        dd.x = x;                    // keep the model in sync for the lasso hit-test
        return x;
      })
      .attr('cy', (dd: Dot) => { dd.y = dd.baseY ?? dd.y; return dd.y; });
  }

  /** Style the brush handles (white rounded rects with a drop-shadow) and
   *  position the decorative grip lines on top — matching the popup. */
  private positionBrushHandles() {
    const bg = this.dayBrushGroup;
    if (!bg) return;
    const top = this.dayTrackTop;
    bg.selectAll('.handle')
      .attr('fill', '#fff').attr('rx', 4).attr('ry', 4)
      .attr('y', top + (DAY_BRUSH_H - 22) / 2).attr('height', 22)
      .style('filter', 'url(#dayBrushShadow)');

    const hs = 10; // handleSize
    const y1 = top + (DAY_BRUSH_H - 10) / 2;
    const y2 = top + (DAY_BRUSH_H + 10) / 2;
    const wx = parseFloat(bg.select('.handle--w').attr('x'));
    const ex = parseFloat(bg.select('.handle--e').attr('x'));
    const place = (cls: string, x: number) =>
      this.svg.select('.' + cls).attr('x1', x).attr('x2', x).attr('y1', y1).attr('y2', y2);
    if (!isNaN(wx)) { place('day-handle-w1', wx + hs / 2 - 2); place('day-handle-w2', wx + hs / 2 + 2); }
    if (!isNaN(ex)) { place('day-handle-e1', ex + hs / 2 - 2); place('day-handle-e2', ex + hs / 2 + 2); }
  }

  /** Tear down the lasso selection + panel WITHOUT touching the day brush or
   *  repainting the resting state (the day brush highlight follows). */
  private clearLassoOnly() {
    this.lassoSelected.clear();
    this.disposeLassoPanel();
    this.lassoPanelData = null;
    this.svg?.select('.du-scraii-beeswarm-lasso-path')
      .attr('d', '')
      .style('display', 'none')
      .style('opacity', 1)
      .attr('fill', 'rgba(33, 150, 243, 0.15)')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5');
  }

  /* ──────────── 6. Sankey Links ──────────── */
  private drawSankeyLinks() {
    const self = this;
    const g = this.svg.append('g').attr('class', 'sankey-links');

    // Stagger sankey link fade-ins by column so each layer feels like it
    // "flows" into the next. 'hub-lane' is used when the bridge role is
    // unbound and the engaged hub flows straight into the lane stubs.
    const layerDelay: Record<string, number> = {
      'sp-hub':    100,
      'hub-fill':  240,
      'fill-lane': 380,
      'hub-lane':  240,
    };

    const drawLinkGroup = (links: SankeyLink[], className: string,
                           sources: NodePos[], targets: NodePos[]) => {
      links.forEach((link, i) => {
        const mid = (link.x0 + link.x1) / 2;
        const path = `M ${link.x0},${link.y0}
          C ${mid},${link.y0} ${mid},${link.y1} ${link.x1},${link.y1}
          L ${link.x1},${link.y1 + link.h1}
          C ${mid},${link.y1 + link.h1} ${mid},${link.y0 + link.h0} ${link.x0},${link.y0 + link.h0} Z`;

        const src = sources.find(s => s.id === link.s);
        const tgt = targets.find(t => t.id === link.t);
        const srcName = src?.name || link.s;
        const tgtName = tgt?.name || link.t;
        const srcTotal = src?.n || 0;
        const pctOfSource = srcTotal > 0 ? ((link.v / srcTotal) * 100).toFixed(2) : '0.0';

        const pathEl = g.append('path')
          .attr('class', `sankey-link ${className} link-${link.s}-${link.t}`)
          .attr('data-link-s', link.s)
          .attr('data-link-t', link.t)
          .attr('data-link-kind', className)
          .attr('d', path)
          .attr('fill', link.color)
          .attr('opacity', 0)
          .style('transition', 'opacity .2s');
        pathEl.transition()
          .duration(450)
          .delay((layerDelay[className] || 100) + i * 20)
          .ease(d3.easeCubicOut)
          .attr('opacity', 0.18);
        pathEl
          .on('mouseenter', function (this: any, ev: MouseEvent) {
            // Light up the route through this specific link; restore on leave.
            // applyFilters is the source of truth for the resting state, so we
            // call it on mouseleave to respect any click-pinned selection.
            const hoverRoute = self.getLinkRoute(link.s, link.t, className);
            if (hoverRoute) self.applyRouteHighlight(hoverRoute);
            self.showTooltip(ev, {
              header: 'Link Details',
              items: [
                { bulletColor: link.color, text: `${srcName} → ${tgtName}: ${link.v.toLocaleString()}` },
                { bulletColor: link.color, text: `${pctOfSource}% of source` }
              ]
            });
          })
          .on('mousemove', (ev: MouseEvent) => self.moveTooltip(ev))
          .on('mouseleave', function (this: any) {
            self.applyFilters();
            self.hideTooltip();
          });
      });
    };

    drawLinkGroup(this.spHubLinks,   'sp-hub',    this.spNodes,    this.hubNodes);
    if (this.roleLevelIndex.bridge !== null) {
      drawLinkGroup(this.hubFillLinks, 'hub-fill',  this.hubNodes,   this.bridgeBars);
      drawLinkGroup(this.fillLaneLinks,'fill-lane', this.bridgeBars, this.laneBars);
    } else {
      drawLinkGroup(this.hubLaneLinks, 'hub-lane',  this.hubNodes,   this.laneBars);
    }
  }

  /* ──────────── 7. SP Nodes ──────────── */
  private drawSPNodes() {
    const self = this;
    const g = this.svg.append('g').attr('class', 'sp-nodes');

    const spLayerTotal = this.spNodes.reduce((s, n) => s + n.n, 0);

    this.spNodes.forEach((sp, i) => {
      const bar = g.append('rect')
        .attr('class', `sp-bar sp-${sp.id}`)
        .attr('x', COL_SP)
        // Grow-in animation: bars start collapsed at their own bottom edge.
        .attr('y', sp.y + sp.h).attr('height', 0)
        .attr('width', BAR_W)
        .attr('fill', sp.color).attr('rx', 3)
        .style('cursor', 'pointer').style('transition', 'opacity .2s');
      bar.transition()
        .duration(500)
        .delay(60 + i * 40)
        .ease(d3.easeCubicOut)
        .attr('y', sp.y)
        .attr('height', sp.h);
      bar
        .on('click', () => self.toggleSP(sp.id))
        .on('mouseenter', (ev: MouseEvent) => {
          self.showTooltip(ev, {
            header: 'Node Details',
            items: [
              { bulletColor: sp.color, text: `${sp.name}: ${sp.n.toLocaleString()}` },
              { bulletColor: sp.color, text: `Percentage: ${sp.pct}%` },
              { bulletColor: sp.color, text: `Layer Total: ${spLayerTotal.toLocaleString()}` }
            ]
          });
        })
        .on('mousemove', (ev: MouseEvent) => self.moveTooltip(ev))
        .on('mouseleave', () => self.hideTooltip());

      this.drawAboveBarLabel(g, COL_SP, sp.y, `sp-label sp-label-${sp.id}`, sp.pct, sp.n, sp.name,
        { type: 'sp', levelIndex: 0, data: sp });
    });
  }

  /**
   * Renders a 2-line label sitting just above a bar, anchored to the bar's left edge:
   *   line 1: <b>{pct}%</b> ({n})  — bold dark percentage + smaller gray count
   *   line 2: {name} ⌃              — gray sub-label + clickable navigation chevron
   * Used by SP / HUB / BRIDGE / LANE columns to keep label styling consistent.
   *
   * `navData` is the payload that will be console.logged when the user clicks
   * the chevron — pass the node object (sp / hub / bridge / lane bar).
   */
  private drawAboveBarLabel(g: any, x: number, barY: number, classes: string,
                            pct: number, n: number, name: string,
                            navData?: { type: string; levelIndex: number; data: any }) {
    const truncName = name.length > 22 ? name.slice(0, 21) + '…' : name;

    // Line 1: pct% + (n)
    const t1 = g.append('text')
      .attr('class', classes)
      .attr('x', x)
      .attr('y', barY - 18)
      .attr('font-family', 'Poppins, sans-serif')
      .style('transition', 'opacity .2s');
    t1.append('tspan')
      .attr('fill', '#101D42')
      .attr('font-size', 14)
      .attr('font-weight', 700)
      .text(`${pct}%`);
    t1.append('tspan')
      .attr('fill', '#8A98AB')
      .attr('font-size', 12)
      .attr('font-weight', 500)
      .attr('dx', 6)
      .text(`(${n})`);

    // Line 2: name (+ optional chevron). When `navData` is provided we wrap
    // the name + chevron in a single nav-link <g> so hovering EITHER element
    // turns both blue, and clicking EITHER fires the same navigation.
    const NAME_DEFAULT_FILL = '#8A98AB';
    const NAV_HOVER_BLUE    = '#1463de';
    const NAV_HOVER_DEEP    = '#1463de';
    const NAV_STROKE_W      = 1.6;
    const NAV_STROKE_HOVER  = 2;

    const labelGroup = navData
      ? g.append('g')
          .attr('class', `${classes} nav-link`)
          .style('cursor', 'pointer')
      : g;

    // `labelCenterY` is the shared vertical centre used by both the name
    // text and the chevron icon. Instead of `dominant-baseline:middle`
    // (which centres the em-box and shifts visibly whenever the label has
    // a descender — g/y/p), we pin the text's BASELINE so the cap-height
    // centre (~baseline − 4 for an 11px font) always lands on
    // labelCenterY. The icon's centre (path mid at y=5) is then offset
    // by the same amount, so both line up regardless of which letters
    // appear in the label.
    const labelCenterY = barY - 8;
    const FONT_SIZE    = 11;
    // For an 11px font the cap-height is ~8px, so the cap-centre sits ~4px
    // above the baseline — push the baseline 4px DOWN from labelCenterY.
    const CAP_TO_CENTER = 4;
    const nameText = labelGroup.append('text')
      .attr('class', classes)
      .attr('x', x)
      .attr('y', labelCenterY + CAP_TO_CENTER)
      .attr('fill', NAME_DEFAULT_FILL)
      .attr('font-size', FONT_SIZE)
      .attr('font-family', 'Poppins, sans-serif')
      .style('transition', 'opacity .2s, fill .15s')
      .text(truncName);

    // Clickable navigation chevron next to the name.
    if (navData) {
      // Measure the rendered name so the icon sits right after it.
      let nameWidth = 0;
      try {
        nameWidth = nameText.node()!.getBBox().width;
      } catch {
        // getBBox can throw if the element isn't in the DOM yet — fall back
        // to a conservative estimate (~6px per char at font-size 11).
        nameWidth = truncName.length * 6;
      }
      const iconX = x + nameWidth + 4;
      // Chevron path span: y = 2..8 (centre 5). Place the icon so its chevron
      // centre lands exactly on labelCenterY = the name's centre — keeps
      // the small original icon visually align-items:center with the name.
      const iconY = labelCenterY - 5;

      const icon = labelGroup.append('g')
        .attr('class', `${classes} nav-icon`)
        .attr('transform', `translate(${iconX}, ${iconY})`);

      // Native browser tooltip on hover (SVG uses <title>, not the HTML
      // `title` attribute).
      icon.append('title').text('Navigate to Patient List');

      // Transparent hit area — keeps the click target bigger than the glyph.
      icon.append('rect')
        .attr('x', -2).attr('y', -2)
        .attr('width', 14).attr('height', 14)
        .attr('fill', 'transparent');

      // Right-pointing chevron (Material-ish), original 4×6 size — kept
      // small so it doesn't visually dominate the 11px label text.
      const chevron = icon.append('path')
        .attr('d', 'M3 2 L7 5 L3 8')
        .attr('fill', 'none')
        .attr('stroke', NAV_HOVER_BLUE)
        .attr('stroke-width', NAV_STROKE_W)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round');

      // Hover + click are wired to the WHOLE nav-link group so hovering the
      // name OR the chevron lights up both (text turns nav-blue, chevron
      // darkens) and clicking either dispatches the same navigation.
      labelGroup
        .on('mouseenter', () => {
          nameText.attr('fill', NAV_HOVER_BLUE);
          chevron.attr('stroke', NAV_HOVER_DEEP).attr('stroke-width', NAV_STROKE_HOVER);
        })
        .on('mouseleave', () => {
          nameText.attr('fill', NAME_DEFAULT_FILL);
          chevron.attr('stroke', NAV_HOVER_BLUE).attr('stroke-width', NAV_STROKE_W);
        })
        .on('click', (ev: MouseEvent) => {
          // Stop the click from bubbling so the underlying bar's click
          // handler (toggleSP / toggleLane) doesn't also run.
          ev.stopPropagation();
          this.logNodeNavigation(navData.levelIndex, navData.data, navData.type);
        });
    }
  }

  /**
   * Single entry point for every navigation click on this chart:
   *   - SP / HUB / BRIDGE / LANE node chevron        → source: 'node'
   *   - Lasso panel HEADER chevron (all selected)    → source: 'lasso-all'
   *   - Lasso panel ROW chevron (by SP / by outcome) → source: 'lasso-row'
   *
   * Centralising the payload here makes it the one place to evolve (emit an
   * @Output, route to a detail page, hit an API, etc.). Today it logs.
   */
  onNavigation(event: {
    source: 'node' | 'lasso-all' | 'lasso-row';
    type: 'sp' | 'hub' | 'bridge' | 'lane' | 'all';
    data: any;            // the clicked node / row object
    column?: string | null; // raw-data column the click maps to (node clicks)
    nodeName?: string;      // clicked node's display name
    count: number;
    patientIds: string[];   // array of DU_PATIENT_IDs
  }) {
    
    let objData = {
      ...event,
      patient_key: event.patientIds.join(',')
    }

    // Open the shared BasicPopupGridComponent. The component handles its own
    // loader + API call lifecycle: it shows a spinner while the POST is in
    // flight, then renders the response in an ag-grid. Replace the TODOs
    // below with the real backend api_key and AG Grid columnDefs when ready.
    const dialogData: BasicPopupGridDialogData = {
      heading: 'Patient List',
      // TODO: replace 0 with the real api_key for this chart's patient-detail endpoint.
      apiKey: 200352,
      endpoint: 'PSS/get-pss-data',
      reportType: this.filterService.report_type || 'D',
      payload: {
        patient_key: objData.patient_key,
        nav_type: event.type,
        nav_source: event.source,
        nav_column: event.column || null,
        nav_value: event.nodeName || null,
      },
      columnDefs: [
        { field: 'du_patient_id',           headerName: 'DU Patient ID',           textAlign: 'left', format: 'string' },
        { field: 'prescriber_name',         headerName: 'Prescriber Name',         textAlign: 'left', format: 'string' },
        { field: 'referral_source',         headerName: 'Referral Source',         textAlign: 'left', format: 'string' },
        { field: 'patient_initiation_date', headerName: 'Patient Initiation Date', textAlign: 'left', format: 'date'   },
        {
          field: 'status_group',
          headerName: 'Status Group',
          textAlign: 'left',
          format: 'string',
          width: 200,
          // Display the value from `status_group` but pick the icon by
          // matching the row's `case_status_group` field against `col_value`.
          compareField: 'case_status_group',
          conditionalFormat: [
            { col_value: 'PENDING',                isSelect: true, icon: 'status', color: '#fecc4a' },
            { col_value: 'CANCELLED',              isSelect: true, icon: 'status', color: '#fe5454' },
            { col_value: 'DISCONTINUED',           isSelect: true, icon: 'status', color: '#fb3ca3' },
            { col_value: 'ACTIVE',                 isSelect: true, icon: 'status', color: '#3ac97c' },
            { col_value: 'ACTIVE PENDING',         isSelect: true, icon: 'status', color: '#3ac97c' },
            { col_value: 'PAP',                    isSelect: true, icon: 'status', color: '#8905f1' },
            { col_value: 'BRIDGE',                 isSelect: true, icon: 'status', color: '#e368c0' },
            { col_value: 'COPAY',                  isSelect: true, icon: 'status', color: '#0833ca' },
            { col_value: 'NURSING',                isSelect: true, icon: 'status', color: '#25d2ea' },
            { col_value: 'QUICK START',            isSelect: true, icon: 'status', color: '#bbce3f' },
            { col_value: 'CANCELLED - TRANSITION', isSelect: true, icon: 'status', color: '#fd5454' },
            { col_value: 'CT',                     isSelect: true, icon: 'status', color: '#9a40c5' },
            { col_value: 'DENIED',                 isSelect: true, icon: 'status', color: '#af4646' },
            { col_value: 'CMM-APPROVED',           isSelect: true, icon: 'status', color: '#80b55f' },
            { col_value: 'CMM-DENIED',             isSelect: true, icon: 'status', color: '#c35b0f' },
            { col_value: 'CMM-INACTIVE',           isSelect: true, icon: 'status', color: '#c55ece' },
            { col_value: 'CMM-PENDING',            isSelect: true, icon: 'status', color: '#fdbf63' },
            { col_value: 'MISSING INFORMATION',    isSelect: true, icon: 'status', color: '#fdbf63' },
            { col_value: 'DEBIT CARD',             isSelect: true, icon: 'status', color: '#7458e6' },
            { col_value: 'DORMANT',                isSelect: true, icon: 'status', color: '#e267bf' },
            { col_value: 'IAP',                    isSelect: true, icon: 'status', color: '#4732a0' },
            { col_value: 'TRANSITION FREE GOOD',   isSelect: true, icon: 'status', color: '#89c9e4' },
            { col_value: 'Random',                 isSelect: true, icon: 'status' },
            { col_value: 'CANCELED',               isSelect: true, icon: 'status', color: '#fe5454' },
          ],
        },
        { field: 'payer_type',              headerName: 'Payer Type',              textAlign: 'left', format: 'string' },
        { field: 'hub_engagement_status',   headerName: 'HUB Engagement Status',   textAlign: 'left', format: 'string' },
        { field: 'cancellation_date',       headerName: 'Cancellation Date',       textAlign: 'left', format: 'date'   },
      ]
    };
    this.dialog.open(BasicPopupGridComponent, {
      data: dialogData,
      panelClass: 'scraii-bgrid-list-dialog',
      width: '90vw',
      maxWidth: '1400px',
      height: '80vh',
      disableClose: false,
    });

// console.log(objData)
    // Set Navigation
    // const cfg = this.item.config;
    // const config = cfg?.nav_configuration.controls?.[0];
    // let nav = {
    //   dbJSON: config?.dbJSON,
    //   s3JSON: config?.s3JSON,
    //   pageName: config?.pageName,
    //   isDashboardLevel: config?.isDashboardLevel
    // }
    // sessionStorage.setItem("navDetailInfo", JSON.stringify(nav));
    // this.routerHandlerService.storingPayloadAndFilterForGrid(config, objData, '');

    // let query = {
    //   tab_id: 0,
    //   cl_key: this.clientKey,
    //   md_key: this.moduleKey,
    //   ds_key: this.dashboardKey
    // }

    // // Build a per-click identifier: "HAR" + current time HHMMSS, then encrypt
    // // it via the navigation service (which routes through the shared
    // // cryptoService used elsewhere for URL/query encryption).
    // const now = new Date();
    // const pad = (n: number) => String(n).padStart(2, '0');
    // const harId = `HAR${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    // const encryptedHar = this.navigationService.encrypedData({ x: harId }).x;

    // this.navigationService.routerNavigate(`dashboard/${this.moduleName}/${encryptedHar}`, query);
  }

  /**
   * Resolves the data column + patient-ids for a node-chevron click and hands
   * the unified payload to `onNavigation`.
   */
  private logNodeNavigation(levelIndex: number, nodeData: any, type?: string) {
    const rows: any[] = Array.isArray(this.data) ? this.data : [];
    const targetName = String(nodeData?.name ?? '').trim();
    const targetId   = String(nodeData?.id ?? '').trim();

    const f = this.props.fields;
    const levels = Array.isArray(f.levels) ? f.levels : [];
    const columnKey    = levels[levelIndex] || null;
    const patientIdKey = f.patientId || null;
    // Preserve the legacy 'sp'/'hub'/'bridge'/'lane' type for downstream
    // consumers when a caller passes it; otherwise fall back to a generic
    // level-N tag derived from the position. Either way the column lookup
    // is purely positional.
    const navType = (type ?? `level_${levelIndex + 1}`) as any;

    if (rows.length === 0 || !columnKey) {
      this.onNavigation({
        source: 'node',
        type: navType,
        data: nodeData,
        column: columnKey,
        nodeName: targetName,
        count: 0,
        patientIds: []
      });
      return;
    }

    // Per-hub downstream model: level_3 / level_4 nodes belong to a specific
    // hub (carried on the node as `hub`). Scope a deeper-level click to that
    // hub's rows so the count matches the displayed (hub-specific) node.
    const hubColKey = levels[this.roleLevelIndex.hub] || null;
    const nodeHubId = String(nodeData?.hub ?? '');
    const hubScoped =
      !!nodeHubId &&
      !!hubColKey &&
      levelIndex > this.roleLevelIndex.hub;
    const isInScope = (r: any) =>
      !hubScoped || this.toId(String(r[hubColKey!] ?? '').trim()) === nodeHubId;

    // Generic top-N rollup: the first level can roll smaller groups into a
    // single bucket with id 'other'. Its members are every row whose first-
    // level value isn't in the named set.
    const isOtherRollup = levelIndex === 0 && targetId === 'other';
    const notFilledLabel = (this.props.labels.notFilledName || '').toLowerCase();

    const matchingRows = rows.filter(r => {
      if (!isInScope(r)) return false;
      const cell = String(r[columnKey] ?? '').trim();
      if (isOtherRollup) {
        return cell.length > 0 && !this.namedSPNames.has(cell);
      }
      // Any level whose displayed label matches the configured empty-cell
      // rollup label (e.g. "Not Filled") also captures rows with an empty
      // value at that level — that's how buildChartData synthesises the
      // bucket.
      if (!cell && targetName.toLowerCase() === notFilledLabel) return true;
      return cell === targetName;
    });

    const patientIds = patientIdKey
      ? matchingRows
          .map(r => String(r[patientIdKey] ?? '').trim())
          .filter(id => id.length > 0)
      : [];

    this.onNavigation({
      source: 'node',
      type: navType,
      data: nodeData,
      column: columnKey,
      nodeName: targetName,
      count: patientIds.length,
      patientIds
    });
  }

  /* ──────────── 8. HUB Nodes ──────────── */
  private drawHUBNodes() {
    const self = this;
    const g = this.svg.append('g').attr('class', 'hub-nodes');

    const hubLayerTotal = this.hubNodes.reduce((s, n) => s + n.n, 0);

    this.hubNodes.forEach((hub, i) => {
      const bar = g.append('rect')
        .attr('class', `hub-bar hub-${hub.id}`)
        .attr('x', COL_HUB)
        .attr('y', hub.y + hub.h).attr('height', 0)
        .attr('width', BAR_W)
        .attr('fill', hub.color).attr('rx', 3)
        .style('cursor', 'pointer')
        .style('transition', 'opacity .2s');
      bar.transition()
        .duration(500)
        .delay(140 + i * 50)
        .ease(d3.easeCubicOut)
        .attr('y', hub.y)
        .attr('height', hub.h);
      bar
        .on('click', () => self.toggleHub(hub.id))
        .on('mouseenter', (ev: MouseEvent) => {
          self.showTooltip(ev, {
            header: 'Node Details',
            items: [
              { bulletColor: hub.color, text: `${hub.name}: ${hub.n.toLocaleString()}` },
              { bulletColor: hub.color, text: `Percentage: ${hub.pct}%` },
              { bulletColor: hub.color, text: `Layer Total: ${hubLayerTotal.toLocaleString()}` }
            ]
          });
        })
        .on('mousemove', (ev: MouseEvent) => self.moveTooltip(ev))
        .on('mouseleave', () => self.hideTooltip());

      this.drawAboveBarLabel(g, COL_HUB, hub.y, `hub-label hub-label-${hub.id}`, hub.pct, hub.n, hub.name,
        { type: 'hub', levelIndex: 1, data: hub });
    });
  }

  /* ──────────── 9. Bridge Bars ──────────── */
  private drawBridgeBars() {
    const self = this;
    const g = this.svg.append('g').attr('class', 'bridge-bars');

    const bridgeLayerTotal = this.bridgeBars.reduce((s, n) => s + n.n, 0);

    this.bridgeBars.forEach((bar, i) => {
      const rect = g.append('rect')
        .attr('class', `bridge-bar bridge-${bar.id}`)
        .attr('x', COL_FILL)
        .attr('y', bar.y + bar.h)
        .attr('width', BAR_W)
        .attr('height', 0)
        .attr('fill', bar.color)
        .attr('rx', 3)
        .style('cursor', 'pointer')
        .style('transition', 'opacity .2s');
      rect.transition()
        .duration(500)
        .delay(220 + i * 50)
        .ease(d3.easeCubicOut)
        .attr('y', bar.y)
        .attr('height', bar.h);
      rect
        .on('click', () => self.toggleLane(bar.id))
        .on('mouseenter', function (ev: MouseEvent) {
          self.showTooltip(ev, {
            header: 'Node Details',
            items: [
              { bulletColor: bar.color, text: `${bar.name}: ${bar.n.toLocaleString()}` },
              { bulletColor: bar.color, text: `Percentage: ${bar.pct}%` },
              { bulletColor: bar.color, text: `Layer Total: ${bridgeLayerTotal.toLocaleString()}` }
            ]
          });
        })
        .on('mousemove', (ev: MouseEvent) => self.moveTooltip(ev))
        .on('mouseleave', () => self.hideTooltip());

      this.drawAboveBarLabel(g, COL_FILL, bar.y, `bridge-label bridge-label-${bar.id}`, bar.pct, bar.n, bar.name,
        { type: 'bridge', levelIndex: 2, data: bar });
    });
  }

  /* ──────────── 9b. Lane Stub Bars ──────────── */
  private drawLaneBars() {
    const self = this;
    const d = this.chartData;
    const g = this.svg.append('g').attr('class', 'lane-bars');

    const laneLayerTotal = this.laneBars.reduce((s, n) => s + n.n, 0);

    this.laneBars.forEach((bar, i) => {
      const lane = d.lanes.find(l => l.id === bar.id);
      const groupClass = lane ? `lane-bar-group-${lane.fillGroup}` : '';
      const rect = g.append('rect')
        .attr('class', `lane-bar lane-bar-${bar.id} ${groupClass}`)
        .attr('x', COL_LANE)
        .attr('y', bar.y + bar.h)
        .attr('width', BAR_W)
        .attr('height', 0)
        .attr('fill', bar.color)
        .attr('rx', 3)
        .style('cursor', 'pointer');
      rect.transition()
        .duration(500)
        .delay(320 + i * 40)
        .ease(d3.easeCubicOut)
        .attr('y', bar.y)
        .attr('height', bar.h);
      rect
        .style('transition', 'opacity .2s')
        .on('click', () => {
          if (lane) self.toggleLane(lane.fillGroup);
        })
        .on('mouseenter', (ev: MouseEvent) => {
          self.showTooltip(ev, {
            header: 'Node Details',
            items: [
              { bulletColor: bar.color, text: `${bar.name}: ${bar.n.toLocaleString()}` },
              { bulletColor: bar.color, text: `Percentage: ${bar.pct}%` },
              { bulletColor: bar.color, text: `Layer Total: ${laneLayerTotal.toLocaleString()}` }
            ]
          });
        })
        .on('mousemove', (ev: MouseEvent) => self.moveTooltip(ev))
        .on('mouseleave', () => self.hideTooltip());

      // Above-bar label (matches SP / HUB / BRIDGE styling) — replaces the
      // inline "X% (n) Name" that used to sit inside the swim lane.
      this.drawAboveBarLabel(g, COL_LANE, bar.y, `outcome-label outcome-label-${bar.id}`, bar.pct, bar.n, bar.name,
        { type: 'lane', levelIndex: 3, data: bar });
    });
  }

  /* ──────────── 11. Beeswarm Dots ──────────── */
  private drawBeeswarmDots() {
    const self = this;
    const d = this.chartData;
    const cfg = d.cfg;
    // Lay the swarm out ONCE against the FULL day range. The brush zoom is then
    // applied as a cheap viewport remap (applyFocusTransform) rather than a
    // re-run of the force simulation, so dragging the brush moves the data
    // smoothly. Each dot keeps its full-range anchor (baseX/baseY).
    const xScale = this.fullBeeXScale();

    // Generate dots — use real patient data if provided, else synthetic
    this.dots = [];

    if (d.patients && d.patients.length > 0) {
      d.lanes.forEach((lane, li) => {
        // Beeswarm follows the boosted lane bar (the visible swimlane) — not
        // the proportional rect — so dots fill the same y-span as the
        // highlighted last-column node.
        const bar = this.laneBars[li];
        const lanePatients = d.patients!.filter(p => p.lane === lane.id);
        lanePatients.forEach((p, j) => {
          const originalDays = p.days;
          // ≤ 0-day patients live in the dedicated left band (kept to the LEFT
          // of the 0d tick); positive days map onto the day scale.
          const isZero = originalDays <= 0;
          const days = isZero ? 0 : Math.min(originalDays, cfg.maxDays);
          // Anchor the zero cluster AT the node (left edge) so it starts flush
          // with the lane bar and grows rightward; the 0d tick sits where it
          // ends (DOT_X_START + zeroBandWidth).
          const targetX = isZero
            ? DOT_X_START + DOT_R
            : xScale(days);
          const spColor = d.sps.find(s => s.id === p.sp)?.color || '#636e72';
          this.dots.push({
            id: p.id || `dot-${lane.id}-${j}`,
            // Take the fill group from the PATIENT, not the lane. A lane can
            // contain patients with mixed fill statuses (e.g. an outcome lane
            // where some patients are Filled and some are Not Filled); using
            // lane.fillGroup would tag every dot the same and break filled-only
            // stats like the lasso panel's Avg/Median Days.
            sp: p.sp, hub: p.hub, lane: lane.id, fillGroup: p.fillGroup,
            days, originalDays, color: spColor,
            x: targetX, y: bar.y + bar.h / 2
          });
        });
      });
    } else {
      const dayGenerators: Record<string, () => number> = {
        filled: d3.randomLogNormal(3.2, 0.7),
        not_filled: d3.randomLogNormal(3.8, 0.6),
        in_progress: d3.randomLogNormal(2.8, 0.8)
      };
      d.lanes.forEach((lane, li) => {
        const bar = this.laneBars[li];
        const gen = dayGenerators[lane.fillGroup] || d3.randomLogNormal(3.5, 0.7);
        for (let j = 0; j < lane.n; j++) {
          const sp = d.sps[Math.floor(Math.random() * d.sps.length)];
          const originalDays = Math.max(0, gen());
          const days = Math.min(originalDays, cfg.maxDays);
          this.dots.push({
            id: `dot-${lane.id}-${j}`, sp: sp.id, hub: '', lane: lane.id, fillGroup: lane.fillGroup,
            days, originalDays, color: sp.color, x: xScale(days), y: bar.y + bar.h / 2
          });
        }
      });
    }

    // Force simulation per lane — bounds use the boosted lane bar so dots
    // spread across the full visible swimlane height.
    d.lanes.forEach((lane, li) => {
      const bar = this.laneBars[li];
      const laneDots = this.dots.filter(dot => dot.lane === lane.id);
      if (laneDots.length === 0) return;

      // 0d tick x. Zero/negative dots are confined to [DOT_X_START, zeroTick);
      // positive dots stay at/right of it. This keeps the zero cluster fully to
      // the LEFT of 0d instead of collision pushing it past the tick. Full
      // range here — the zoom is a later viewport remap, not a re-layout.
      const zeroTick = DOT_X_START + this.zeroBandWidth;
      const yMin = bar.y + DOT_R;
      const yMax = bar.y + bar.h - DOT_R;

      // Each dot's x ANCHOR = the pixel for its actual day (zero/negative dots
      // sit in the left band). The x-force pulls toward THIS anchor — not the
      // dot's current x — so a dot keeps its day position and the collision
      // spreads the swarm VERTICALLY. (The old code targeted `dd.x`, i.e. the
      // moving position, which is a no-op force: collide then pushed low-day
      // dots far to the right of their value — e.g. a 6-day dot landing past
      // the 23d tick.)
      const anchorXOf = (dd: any) =>
        dd.originalDays <= 0 ? (DOT_X_START + DOT_R) : xScale(dd.days);

      // POSITIVE dots are pinned to the EXACT pixel for their day (fx), so the
      // collision can only spread the swarm VERTICALLY — every bubble lines up
      // precisely with its axis tick. Previously the x-force was soft (0.92)
      // and collision could slide a dot up to MAX_DRIFT px sideways, landing it
      // a few days off its true value (e.g. a 22-day dot rendering where ~25d
      // reads on the scale). Zero/negative dots stay UNPINNED so they can still
      // fan out across the left ≤0 band.
      laneDots.forEach(dd => {
        if (dd.originalDays > 0) (dd as any).fx = xScale(dd.days);
        else                     (dd as any).fx = null;
      });

      // Collide radius bumped (DOT_R + 2.2) so adjacent dots stop with a real
      // gap between them — without this, hovering one dot can visually cover
      // its neighbor and block hover events on it. No x-force: positive dots are
      // pinned (fx) and the zero band is positioned by the clamp below.
      const sim = d3.forceSimulation(laneDots as any)
        .force('x', d3.forceX(anchorXOf).strength((dd: any) => dd.originalDays <= 0 ? 1 : 0))
        .force('y', d3.forceY(bar.y + bar.h / 2).strength(0.06))
        .force('collide', d3.forceCollide(DOT_R + 2.2))
        .stop();

      // Clamp x/y INSIDE the tick loop so the collide force can rebalance
      // against the boundaries each iteration. Positive dots are pinned via fx,
      // so only their y needs clamping; zero/negative dots are bounded to the
      // left band so they never cross the 0d tick.
      for (let k = 0; k < 120; k++) {
        sim.tick();
        for (let i = 0; i < laneDots.length; i++) {
          const dot = laneDots[i];
          const isZero = dot.originalDays <= 0;
          if (isZero) {
            const dxMin = DOT_X_START;
            const dxMax = Math.max(DOT_X_START, zeroTick - DOT_R);
            if (dot.x < dxMin) dot.x = dxMin;
            else if (dot.x > dxMax) dot.x = dxMax;
          }
          if (dot.y < yMin) dot.y = yMin;
          else if (dot.y > yMax) dot.y = yMax;
        }
      }
    });

    // Freeze the full-range layout as each dot's base anchor. The brush zoom
    // remaps display x from baseX through the focus scale (applyFocusTransform),
    // so the swarm pans/zooms smoothly without re-running the simulation.
    this.dots.forEach(dot => { dot.baseX = dot.x; dot.baseY = dot.y; dot.fx = undefined; dot.fy = undefined; });

    // Clip the swarm to the beeswarm x-range so dots that pan out of the focus
    // window (when zoomed via the brush) slide off smoothly instead of
    // overflowing the chart. Recreated each render (the SVG is wiped on render).
    // Clip starts at BEE_LEFT (the lane stub bar's right edge), NOT DOT_X_START,
    // so a leftmost bubble centered on DOT_X_START renders its FULL radius
    // instead of having its left ~DOT_R px sliced off against the clip edge.
    const defs = this.svg.append('defs').attr('class', 'bee-defs');
    defs.append('clipPath').attr('id', 'beeswarmClip')
      .append('rect')
      .attr('x', BEE_LEFT).attr('y', 0)
      .attr('width', BEE_RIGHT - BEE_LEFT).attr('height', this.viewBoxH);

    // Draw dots — start at r=0 and opacity=0, then pop in with a slight
    // stagger so the swarm reads as "arriving" after the sankey + bars settle.
    const dotsG = this.svg.append('g').attr('class', 'beeswarm-dots')
      .attr('clip-path', 'url(#beeswarmClip)');
    const dotsEnter = dotsG.selectAll('circle')
      .data(this.dots)
      .enter()
      .append('circle')
      .attr('class', (dd: Dot) => `dot dot-${dd.sp} dot-lane-${dd.lane}`)
      .attr('cx', (dd: Dot) => dd.x)
      .attr('cy', (dd: Dot) => dd.y)
      .attr('r', 0)
      .attr('fill', (dd: Dot) => dd.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.6)
      .attr('opacity', 0)
      .style('transition', 'opacity .2s');

    dotsEnter.transition()
      .duration(360)
      .delay((_dd: Dot, i: number) => 480 + (i % 80) * 6) // cap stagger to avoid huge tails
      .ease(d3.easeBackOut.overshoot(1.4))
      .attr('r', DOT_R)
      .attr('opacity', 0.82);

    dotsEnter
      .on('mouseenter', function (this: any, ev: MouseEvent, dd: Dot) {
        // While the user is dragging a lasso, the lasso owns the dot's
        // visual state — don't restyle and don't show the tooltip, or the
        // two highlight layers visibly overlap.
        if (self.isLassoDragging) return;
        // Dots that a legend filter has dimmed out are inert: no tooltip,
        // no grow-on-hover. They're visually disabled and should behave
        // that way too — same predicate the lasso uses, so the two stay
        // in lock-step.
        if (!self.getLegendDotMatch()(dd)) return;
        // Grow the dot slightly and brighten it. No DOM reordering (.raise /
        // .lower) on hover — moving the circle in the DOM can cause some
        // browsers to fire spurious mouseenter/leave pairs, which leaves the
        // tooltip and the enlarged size "stuck" when you exit the dot.
        d3.select(this)
          .attr('r', DOT_R + 2)
          .attr('opacity', 1);
        self.showBubbleTooltip(ev, dd);
      })
      .on('mousemove', (ev: MouseEvent, dd: Dot) => {
        if (self.isLassoDragging) return;
        if (!self.getLegendDotMatch()(dd)) return;
        self.moveBubbleTooltip(ev);
      })
      .on('mouseleave', function (this: any, _ev: MouseEvent, dd: Dot) {
        if (self.isLassoDragging) return;
        // Restore the dot's resting style based on the current chart mode:
        //   • lasso active + this dot selected    → 0.85 opacity, r = DOT_R+1
        //   • lasso active + this dot un-selected → 0.06 opacity, r = DOT_R
        //   • no lasso                            → 0.82 opacity, r = DOT_R
        const lassoActive = self.lassoSelected.size > 0;
        const isSelected  = self.lassoSelected.has(dd.id);
        const restingOpacity = !lassoActive ? 0.82 : (isSelected ? 0.85 : 0.06);
        const restingR       = !lassoActive ? DOT_R : (isSelected ? DOT_R + 1 : DOT_R);
        d3.select(this)
          .attr('r', restingR)
          .attr('opacity', restingOpacity);
        self.hideBubbleTooltip();
      });

    // Apply the current focus window (no-op at full range; positions the dots
    // for the active zoom when re-rendering while zoomed).
    this.applyFocusTransform();
  }

  /* ──────────── Bubble (dot) tooltip — private CDK overlay ────────────
   * Why a private overlay instead of DynamicCustomTooltipService?
   *   • The service has one shared overlayRef, so every mouseenter on a new
   *     dot would dispose + recreate the overlay → noticeable flicker and
   *     occasional "no response" when crossing adjacent dots quickly.
   *   • Owning the overlay here lets us REUSE it: when moving from dot A to
   *     dot B we just update bubbleTooltipData and reposition; the embedded
   *     view stays mounted.
   *   • Also keeps bubble hovers independent of node-tooltip / lasso-panel
   *     overlays.
   */
  private showBubbleTooltip(ev: MouseEvent, dd: Dot) {
    // Suppress hover-driven tooltips while the user is actively lassoing —
    // the lasso owns the dots' visual state during the drag.
    if (this.isLassoDragging) return;

    // Cancel any pending hide from a prior mouseleave.
    if (this.bubbleHideTimer) {
      clearTimeout(this.bubbleHideTimer);
      this.bubbleHideTimer = null;
    }

    const d = this.chartData;
    const spInfo = d.sps.find(s => s.id === dd.sp);
    const laneInfo = d.lanes.find(l => l.id === dd.lane);
    const days = Math.round(dd.originalDays ?? dd.days);

    this.zone.run(() => {
      this.bubbleTooltipData = {
        color: dd.color,
        spName: spInfo?.name || dd.sp,
        laneName: laneInfo?.name || dd.lane,
        days,
        atRisk: this.getAtRiskLaneIds().has(dd.lane) && (dd.originalDays ?? dd.days) >= d.cfg.atRisk,
        // dot.id carries the patient id when raw patient data is present;
        // fall back to '—' when only synthetic dots exist (no source row).
        patientId: dd.id && !dd.id.startsWith('dot-') ? dd.id : '—'
      };

      if (!this.bubbleOverlay) {
        // First time: create the overlay and attach the template once.
        this.bubbleOverlay = this.overlay.create({
          positionStrategy: this.buildBubbleOverlayPosition(ev),
          scrollStrategy: this.overlay.scrollStrategies.reposition(),
          panelClass: 'bubble-tooltip-overlay',
          hasBackdrop: false
        });
        this.bubbleOverlay.attach(new TemplatePortal(this.bubbleTooltipTpl, this.viewContainerRef));
      } else {
        // Reuse: just retarget the existing overlay at the new cursor point.
        this.bubbleOverlay.updatePositionStrategy(this.buildBubbleOverlayPosition(ev));
      }
      this.cdr.detectChanges(); // make sure the bound data flushes to the embedded view
    });
  }

  private moveBubbleTooltip(ev: MouseEvent) {
    if (!this.bubbleOverlay) return;
    this.bubbleOverlay.updatePositionStrategy(this.buildBubbleOverlayPosition(ev));
  }

  private hideBubbleTooltip() {
    // Very short grace period — just enough to let an immediately-following
    // mouseenter on an adjacent dot cancel this timer and reuse the overlay,
    // but short enough that exiting a dot feels instant.
    if (this.bubbleHideTimer) clearTimeout(this.bubbleHideTimer);
    this.bubbleHideTimer = setTimeout(() => {
      this.bubbleHideTimer = null;
      this.disposeBubbleOverlay();
      this.zone.run(() => {
        this.bubbleTooltipData = null;
      });
    }, 20);
  }

  private disposeBubbleOverlay() {
    if (this.bubbleOverlay) {
      this.bubbleOverlay.dispose();
      this.bubbleOverlay = null;
    }
  }

  private buildBubbleOverlayPosition(ev: MouseEvent) {
    return this.overlay.position()
      .flexibleConnectedTo({ x: ev.clientX, y: ev.clientY })
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(10)
      .withPositions([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 14, offsetY: -10 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top',    offsetX: 14, offsetY: 14 },
        { originX: 'end',   originY: 'top', overlayX: 'end',   overlayY: 'bottom', offsetX: -14, offsetY: -10 },
        { originX: 'end',   originY: 'top', overlayX: 'end',   overlayY: 'top',    offsetX: -14, offsetY: 14 }
      ]);
  }

  /* ──────────── 12. Lasso Overlay ──────────── */
  private drawLassoOverlay() {
    const self = this;
    const svgEl = this.canvasRef.nativeElement;

    // Lasso path element (drawn on top of overlay rect)
    const lassoPath = this.svg.append('path')
      .attr('class', 'du-scraii-beeswarm-lasso-path')
      .attr('fill', 'rgba(33, 150, 243, 0.15)')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .style('display', 'none')
      .style('pointer-events', 'none');

    let isDragging = false;
    let startX = 0, startY = 0;
    let lastClientX = 0, lastClientY = 0;

    const lasso = d3.drag<SVGRectElement, unknown>()
      .on('start', (event: d3.D3DragEvent<SVGRectElement, unknown, unknown>) => {
        isDragging = false;
        const srcEv = event.sourceEvent as MouseEvent;
        const [x, y] = self.toSvgCoords(srcEv, svgEl);
        startX = x; startY = y;
        lastClientX = srcEv.clientX;
        lastClientY = srcEv.clientY;

        // NOTE: we intentionally do NOT clear the existing polygon on
        // mousedown. d3.drag fires 'start' immediately on mousedown, before
        // we know if the user is really going to drag or just clicked once.
        // Clearing here would wipe a confirmed polygon on every stray click.
        // The polygon is replaced inside the 'drag' handler the moment we
        // detect a real drag (past the 5px threshold).
      })
      .on('drag', (event: d3.D3DragEvent<SVGRectElement, unknown, unknown>) => {
        const srcEv = event.sourceEvent as MouseEvent;
        const [x, y] = self.toSvgCoords(srcEv, svgEl);
        lastClientX = srcEv.clientX;
        lastClientY = srcEv.clientY;

        // 5px threshold — same as scatter-chart — to distinguish click from drag
        if (!isDragging && (Math.abs(x - startX) > 5 || Math.abs(y - startY) > 5)) {
          isDragging = true;
          self.isLassoDragging = true;
          // Tear down any hover tooltip that was open when the drag began so
          // it doesn't linger on top of the lasso highlight.
          self.disposeBubbleOverlay();
          if (self.bubbleHideTimer) {
            clearTimeout(self.bubbleHideTimer);
            self.bubbleHideTimer = null;
          }
          self.zone.run(() => { self.bubbleTooltipData = null; });
          self.lassoPoints = [];
          self.hintVisible = false;
          // The drag is real — reset the existing polygon's path and restore
          // the live-drawing look (so the new draw starts from scratch).
          lassoPath
            .attr('d', '')
            .style('display', 'none')
            .style('opacity', 1)
            .attr('fill', 'rgba(33, 150, 243, 0.15)')
            .attr('stroke', '#2196F3')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '5,5');
        }
        if (!isDragging) return;

        // Skip point if too close to last (keeps path complexity low)
        const last = self.lassoPoints[self.lassoPoints.length - 1];
        if (last) {
          const dx = x - last[0], dy = y - last[1];
          if (dx * dx + dy * dy < 16) return;
        }

        self.lassoPoints.push([x, y]);
        const pathStr = 'M ' + self.lassoPoints.map(p => `${p[0]},${p[1]}`).join(' L ') + ' Z';
        lassoPath.attr('d', pathStr).style('display', null);
        self.highlightLassoDots();
      })
      .on('end', () => {
        if (isDragging && self.lassoPoints.length > 2) {
          self.finalizeLasso(lastClientX, lastClientY);
        }
        // Plain click on the overlay while a selection is active is a no-op:
        // the highlight + panel stay until the user explicitly hits the
        // panel's close (×) button (or the Esc key handler in clearLasso).
        isDragging = false;
        self.isLassoDragging = false;
      });

    // Background rect — receives drag events
    this.svg.append('rect')
      .attr('class', 'du-scraii-beeswarm-lasso-overlay')
      .attr('x', DOT_X_START)
      .attr('y', MARGIN.top)
      .attr('width', BEE_RIGHT - DOT_X_START)
      .attr('height', this.H - MARGIN.top - MARGIN.bottom)
      .attr('fill', 'transparent')
      .attr('pointer-events', 'all')
      .style('cursor', 'crosshair')
      .call(lasso as any);
  }

  private toSvgCoords(ev: MouseEvent, svgEl: SVGSVGElement): [number, number] {
    const rect = svgEl.getBoundingClientRect();
    const x = (ev.clientX - rect.left) / rect.width * W;
    // Map against the full viewBox height (which now includes the day-brush
    // band) so lasso points stay aligned with the dots after the grow.
    const y = (ev.clientY - rect.top) / rect.height * (this.viewBoxH || this.H);
    return [x, y];
  }

  private highlightLassoDots() {
    if (this.lassoPoints.length < 3) return;
    const polygon = this.lassoPoints;
    const pLen = polygon.length;

    // Pre-extract polygon arrays for faster iteration
    const pxs = new Float64Array(pLen);
    const pys = new Float64Array(pLen);
    for (let i = 0; i < pLen; i++) {
      pxs[i] = polygon[i][0];
      pys[i] = polygon[i][1];
    }

    // When any legend (SP / hub / outcome) is active, the lasso must respect
    // that filter — dots the legend has dimmed out are not lasso-eligible.
    // Without this, a user who clicks "Filled" then lassoes still picks up
    // visually-dimmed Pending / Cancelled dots, and those lanes' nav labels
    // are stuck at 0.15 opacity (applyRouteHighlight set them, and
    // applyLassoHighlight intentionally doesn't touch labels).
    const route = this.getActiveRoute();
    const dotMatch = route ? route.dotMatch : (() => true);

    const selected = new Set<string>();
    const dots = this.dots;
    for (let d = 0, len = dots.length; d < len; d++) {
      const dot = dots[d];
      if (!dotMatch(dot)) continue;
      // Inline point-in-polygon for speed
      let inside = false;
      for (let i = 0, j = pLen - 1; i < pLen; j = i++) {
        if (((pys[i] > dot.y) !== (pys[j] > dot.y)) &&
            (dot.x < (pxs[j] - pxs[i]) * (dot.y - pys[i]) / (pys[j] - pys[i]) + pxs[i])) {
          inside = !inside;
        }
      }
      if (inside) selected.add(dot.id);
    }

    // While the user is still drawing, hold every dot at its PRE-LASSO
    // resting opacity so the chart stays as the user already saw it — only
    // the dots actually inside the in-progress polygon get a bump as live
    // feedback. If a legend filter is active, legend-dimmed dots stay
    // dimmed (so they don't re-appear when the user starts a lasso). The
    // full dim (un-selected → 0.06, bars/links → 0.15 / 0.03) kicks in
    // only after the drag ends in `applyLassoHighlight()`.
    const REST_NO_FILTER = 0.82; // matches applyRouteHighlight REST_DOT
    const REST_LEGEND_ON = 0.9;  // matches applyRouteHighlight BRIGHT_DOT
    const DIMMED         = 0.06; // matches applyRouteHighlight DIM_DOT
    this.svg.selectAll('.dot')
      .attr('opacity', (dd: Dot) => {
        if (selected.has(dd.id)) return 1;
        if (!route) return REST_NO_FILTER;
        return route.dotMatch(dd) ? REST_LEGEND_ON : DIMMED;
      })
      .attr('r', (dd: Dot) => selected.has(dd.id) ? DOT_R + 1.5 : DOT_R);
  }

  /** Dot eligibility predicate for the currently active legend filter. When
   *  no legend is active every dot is eligible — otherwise only dots that
   *  match the route (SP / hub / outcome union) qualify. Used by the lasso
   *  so a selection never crosses a legend the user has already narrowed. */
  private getLegendDotMatch(): (dd: Dot) => boolean {
    const route = this.getActiveRoute();
    return route ? route.dotMatch : () => true;
  }

  private finalizeLasso(dropClientX?: number, dropClientY?: number) {
    const dotMatch = this.getLegendDotMatch();
    const selected = new Set<string>();
    this.dots.forEach(dot => {
      if (this.pointInPolygon(dot.x, dot.y, this.lassoPoints) && dotMatch(dot)) {
        selected.add(dot.id);
      }
    });

    if (selected.size === 0) {
      // Nothing inside the polygon → reset everything (also hides the path).
      this.clearLasso();
      return;
    }

    // Keep the drawn polygon visible alongside the highlight + panel. It
    // stays on screen until the user clicks the panel's close button (or
    // Esc), where clearLasso() removes it. Lift the stroke a touch so it
    // reads as a confirmed selection outline rather than a live draw.
    this.svg.select('.du-scraii-beeswarm-lasso-path')
      .style('display', null)
      .style('opacity', 1)
      .attr('stroke-width', 1.6)
      .attr('stroke-dasharray', '6,4')
      .attr('fill', 'rgba(33, 150, 243, 0.08)');

    this.lassoSelected = selected;
    this.applyLassoHighlight();
    this.buildLassoPanel(dropClientX, dropClientY);
  }

  private applyLassoHighlight() {
    this.svg.selectAll('.dot')
      .attr('opacity', (dd: Dot) => this.lassoSelected.has(dd.id) ? 0.85 : 0.06)
      .attr('r', (dd: Dot) => this.lassoSelected.has(dd.id) ? DOT_R + 1 : DOT_R);

    this.svg.selectAll('.sankey-link').attr('opacity', 0.03);
    this.svg.selectAll('.sp-bar').style('opacity', 0.15);
    this.svg.selectAll('.bridge-bar').style('opacity', 0.15);
    this.svg.selectAll('.lane-bar').style('opacity', 0.15);
  }

  private buildLassoPanel(dropClientX?: number, dropClientY?: number) {
    const selectedDots = this.dots.filter(d => this.lassoSelected.has(d.id));
    if (selectedDots.length === 0) return;

    // Avg / median days for the lasso panel use EVERY lassoed bubble (Filled,
    // Not Filled, Pending — all of them), since the panel is about the user's
    // current selection, not the global "Median time to fill" metric.
    // We use `originalDays` (the unclamped raw time) so patients past
    // `maxDays` don't all collapse to the same clamped value.
    const allDays = selectedDots
      .map(d => d.originalDays ?? d.days)
      .sort((a, b) => a - b);

    // Keep integers as-is; round non-integers to at most 2 decimals.
    // (5 → 5, 5.234 → 5.23, 5.0 → 5)
    const trim2 = (n: number) => Math.round(n * 100) / 100;

    let avgDays = 0;
    let medianDays = 0;
    if (allDays.length > 0) {
      avgDays = trim2(allDays.reduce((a, b) => a + b, 0) / allDays.length);
      const mid = Math.floor(allDays.length / 2);
      const rawMedian = allDays.length % 2 === 1
        ? allDays[mid]
        : (allDays[mid - 1] + allDays[mid]) / 2;
      medianDays = trim2(rawMedian);
    }
    // Only at-risk-qualifying bubbles past the median count as "at risk" —
    // same rule used by the bubble tooltip badge and the visual at-risk zone.
    const atRiskIds = this.getAtRiskLaneIds();
    const atRiskCount = selectedDots.filter(d =>
      atRiskIds.has(d.lane) && d.days >= this.chartData.cfg.atRisk
    ).length;

    // By SP
    const bySPMap: Record<string, number> = {};
    selectedDots.forEach(d => bySPMap[d.sp] = (bySPMap[d.sp] || 0) + 1);
    const maxSP = Math.max(1, ...Object.values(bySPMap));
    const bySP = Object.entries(bySPMap)
      .map(([id, count]) => {
        const sp = this.chartData.sps.find(s => s.id === id);
        return sp ? { id, name: sp.name, color: sp.color, count, barPct: (count / maxSP) * 100 } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);

    // By outcome
    const byLaneMap: Record<string, number> = {};
    selectedDots.forEach(d => byLaneMap[d.lane] = (byLaneMap[d.lane] || 0) + 1);
    const maxLane = Math.max(1, ...Object.values(byLaneMap));
    const byLane = Object.entries(byLaneMap)
      .map(([id, count]) => {
        const lane = this.chartData.lanes.find(l => l.id === id);
        return lane ? { id, name: lane.name, color: lane.color, count, barPct: (count / maxLane) * 100 } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);

    this.lassoPanelData = {
      count: selectedDots.length,
      avgDays, medianDays, atRisk: atRiskCount,
      bySP, byLane
    };

    // Pin the panel to the drop point. Fall back to selection centroid if no drop coords.
    let anchorX: number;
    let anchorY: number;
    if (typeof dropClientX === 'number' && typeof dropClientY === 'number') {
      anchorX = dropClientX;
      anchorY = dropClientY;
    } else {
      const svgEl = this.canvasRef.nativeElement;
      const svgRect = svgEl.getBoundingClientRect();
      const xs = selectedDots.map(d => d.x);
      const ys = selectedDots.map(d => d.y);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = Math.min(...ys);
      anchorX = svgRect.left + (cx / W) * svgRect.width;
      anchorY = svgRect.top + (cy / (this.viewBoxH || this.H)) * svgRect.height;
    }

    // Tear down any previous overlay before opening a fresh one at the new point.
    this.disposeLassoPanel();

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo({ x: anchorX, y: anchorY })
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(12)
      .withPositions([
        // Prefer below + slightly right of the drop point; fall back to above / left.
        { originX: 'start',  originY: 'bottom', overlayX: 'start',  overlayY: 'top',    offsetX: 8,  offsetY: 8 },
        { originX: 'start',  originY: 'top',    overlayX: 'start',  overlayY: 'bottom', offsetX: 8,  offsetY: -8 },
        { originX: 'end',    originY: 'bottom', overlayX: 'end',    overlayY: 'top',    offsetX: -8, offsetY: 8 },
        { originX: 'end',    originY: 'top',    overlayX: 'end',    overlayY: 'bottom', offsetX: -8, offsetY: -8 }
      ]);

    this.lassoPanelOverlay = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'lasso-panel-overlay',
      hasBackdrop: false
    });

    this.lassoPanelOverlay.attach(new TemplatePortal(this.lassoPanelTpl, this.viewContainerRef));
    this.cdr.detectChanges();
  }

  private disposeLassoPanel() {
    if (this.lassoPanelOverlay) {
      this.lassoPanelOverlay.dispose();
      this.lassoPanelOverlay = null;
    }
  }

  /**
   * Click handler for the nav chevron in the lasso-panel HEADER — every
   * patient id in the current lasso selection (no SP / outcome filtering).
   */
  onLassoAllNav() {
    const selectedDots = this.dots.filter(d => this.lassoSelected.has(d.id));
    const patientIds = selectedDots
      .map(d => d.id)
      .filter(id => !!id && !id.startsWith('dot-'));

    this.onNavigation({
      source: 'lasso-all',
      type: 'all',
      data: { selectedDots: selectedDots.length, lassoSelected: Array.from(this.lassoSelected) },
      count: patientIds.length,
      patientIds
    });
  }

  /**
   * Click handler for the nav chevron on each lasso-panel ROW (by SP / by outcome).
   */
  onLassoRowNav(type: 'sp' | 'lane', row: { id: string; name: string; color: string; count: number; barPct: number }) {
    const selectedDots = this.dots.filter(d => this.lassoSelected.has(d.id));
    const matching = selectedDots.filter(d =>
      type === 'sp' ? d.sp === row.id : d.lane === row.id
    );
    // dot.id is the patient id (or a synthetic id when there's no raw patient data).
    const patientIds = matching
      .map(d => d.id)
      .filter(id => !!id && !id.startsWith('dot-'));

    this.onNavigation({
      source: 'lasso-row',
      type,
      data: row,
      nodeName: row.name,
      count: patientIds.length,
      patientIds
    });
  }

  clearLasso() {
    this.lassoSelected.clear();
    this.disposeLassoPanel();
    this.lassoPanelData = null;
    this.hintVisible = true;

    // Reset all elements
    this.svg?.selectAll('.dot')
      .attr('opacity', 0.82)
      .attr('r', DOT_R);
    this.svg?.selectAll('.sankey-link').attr('opacity', 0.18);
    this.svg?.selectAll('.sp-bar').style('opacity', 1);
    this.svg?.selectAll('.hub-bar').style('opacity', 1);
    this.svg?.selectAll('.bridge-bar').style('opacity', 1);
    this.svg?.selectAll('.lane-bar').style('opacity', 1);
    // Hide the polygon AND restore the live-draw style attributes so a
    // subsequent lasso starts fresh in the "drawing" look.
    this.svg?.select('.du-scraii-beeswarm-lasso-path')
      .attr('d', '')
      .style('display', 'none')
      .style('opacity', 1)
      .attr('fill', 'rgba(33, 150, 243, 0.15)')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5');

    if (!this.hasActiveFilter()) return;
    this.applyFilters();
  }

  /* ──────────── Point-in-polygon (ray-casting) ──────────── */
  private pointInPolygon(x: number, y: number, polygon: [number, number][]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Compute the route highlighted by the user's current legend selections.
   * Returns null when no filter is active.
   *
   * Facet semantics:
   *   - Within one column (multiple SPs, multiple Lanes…) — UNION (OR).
   *   - Across columns (SP + Outcome, SP + Hub, etc.) — INTERSECTION (AND).
   *
   * Two modes:
   *   1. Single column active → "trace the route" expansion. Picking just
   *      SP=Biologics lights every hub / lane Biologics flows through, so
   *      you can read the full journey end-to-end.
   *   2. Two or more columns active → strict intersection. The node sets
   *      (sps/hubs/fills/lanes) are derived from the dots that pass the
   *      intersection, so bars + labels + sankey links + bubbles all agree
   *      with the lasso predicate. Without this, SP=Biologics + Outcome=
   *      Filled would union-highlight every Filled SP and every Biologics
   *      hub, while only Biologics-AND-Filled dots are actually selected.
   */
  private getActiveRoute(): {
    sps: Set<string>;
    hubs: Set<string>;
    fills: Set<string>;
    lanes: Set<string>;
    dotMatch: (dd: Dot) => boolean;
  } | null {
    if (!this.chartData) return null;
    if (!this.hasActiveFilter()) return null;

    // A column where the user has selected EVERY available chip is
    // logically unfiltered — "all SPs" carries the same meaning as "no SP
    // filter", and both should produce the unfiltered route. Without this,
    // a user who clicks every legend chip ends up with a non-null route
    // that dims items the filter doesn't reach (e.g. the non-engaged hub's
    // nav label drops to 0.15 because activeHubs is empty), even though
    // they've explicitly asked for "show me everything".
    const allSpIds   = this.spList.map(s => s.id);
    const allLaneIds = this.fillList.map(f => f.id);
    const allHubIds  = this.chartData.hubs.map(h => h.id);
    const isFullSet = (set: Set<string>, ids: string[]) =>
      ids.length > 0 && set.size === ids.length && ids.every(id => set.has(id));

    const effSPs   = isFullSet(this.activeSPs,   allSpIds)   ? new Set<string>() : this.activeSPs;
    const effHubs  = isFullSet(this.activeHubs,  allHubIds)  ? new Set<string>() : this.activeHubs;
    const effLanes = isFullSet(this.activeLanes, allLaneIds) ? new Set<string>() : this.activeLanes;
    if (effSPs.size === 0 && effHubs.size === 0 && effLanes.size === 0) return null;

    // Per-column dot predicates. Each column contributes an OR-list; the
    // final dotMatch ANDs across columns.
    const cols: Array<Array<(dd: Dot) => boolean>> = [];
    if (effSPs.size > 0) {
      cols.push(Array.from(effSPs).map(sp => (dd: Dot) => dd.sp === sp));
    }
    if (effHubs.size > 0) {
      // Dots now carry their hub, so a hub selection matches the patients
      // that actually flow through that hub — every hub has downstream dots.
      cols.push(Array.from(effHubs).map(hub => (dd: Dot) => dd.hub === hub));
    }
    if (effLanes.size > 0) {
      cols.push(Array.from(effLanes).map(lane => (dd: Dot) => dd.fillGroup === lane));
    }
    const dotMatch = (dd: Dot) => cols.every(arr => arr.some(fn => fn(dd)));

    const sps   = new Set<string>();
    const hubs  = new Set<string>();
    const fills = new Set<string>();
    const lanes = new Set<string>();

    if (cols.length === 1) {
      // ── Single-column expansion mode ──
      // Same behaviour as before: clicking one legend lights up the full
      // route through the chart so the user can trace its journey.
      effSPs.forEach(sp => {
        sps.add(sp);
        this.chartData.sp_hub.filter(l => l.s === sp).forEach(l => hubs.add(l.t));
        this.dots.filter(d => d.sp === sp).forEach(d => {
          fills.add(d.fillGroup);
          lanes.add(d.lane);
        });
      });
      effHubs.forEach(hub => {
        hubs.add(hub);
        this.chartData.sp_hub.filter(l => l.t === hub).forEach(l => sps.add(l.s));
        // Light this hub's downstream fills/lanes from its own ribbons + dots.
        this.chartData.hub_fill.filter(l => l.s === hub).forEach(l => fills.add(l.t));
        (this.chartData.hub_lane || []).filter(l => l.s === hub).forEach(l => lanes.add(l.t));
        this.dots.filter(d => d.hub === hub).forEach(d => {
          fills.add(d.fillGroup);
          lanes.add(d.lane);
        });
      });
      effLanes.forEach(lane => {
        fills.add(lane);
        this.chartData.lanes.filter(l => l.fillGroup === lane).forEach(l => lanes.add(l.id));
        this.dots.filter(d => d.fillGroup === lane).forEach(d => {
          sps.add(d.sp);
          hubs.add(d.hub);
        });
      });
    } else {
      // ── Multi-column intersection mode ──
      // Derive node sets from the dots that pass the intersection so the
      // chart visuals match the lasso / bubble predicate exactly.
      const matchingDots = this.dots.filter(d => dotMatch(d));
      matchingDots.forEach(d => {
        sps.add(d.sp);
        lanes.add(d.lane);
        fills.add(d.fillGroup);
        hubs.add(d.hub);
      });

      // Explicit-pick step — a user-selected bar must light up even when the
      // intersection has zero matching dots. With no SP filter active, a hub
      // pick also surfaces every upstream SP so the source-side ribbons stay
      // visible; with an SP filter active, only the picked SPs feed it.
      effSPs.forEach(sp => sps.add(sp));
      effLanes.forEach(lane => fills.add(lane));
      effHubs.forEach(hub => {
        hubs.add(hub);
        if (effSPs.size === 0) {
          this.chartData.sp_hub.filter(l => l.t === hub).forEach(l => sps.add(l.s));
        }
      });
    }

    return { sps, hubs, fills, lanes, dotMatch };
  }

  /* ──────────── Filter / route-highlight engine ────────────
   *
   * Clicking any node lights up the full route through the chart — every node
   * the route touches stays bright (its bar, its labels) and every sankey
   * link whose (source, target) both live in the route stays bright. Bubbles
   * matching the route are bright; everything else dims.
   */
  /** Build the route for one sankey link (used by hover-highlight). */
  private getLinkRoute(s: string, t: string, kind: string): {
    sps: Set<string>; hubs: Set<string>; fills: Set<string>; lanes: Set<string>;
    dotMatch: (dd: Dot) => boolean;
  } | null {
    if (!this.chartData) return null;
    const sps = new Set<string>();
    const hubs = new Set<string>();
    const fills = new Set<string>();
    const lanes = new Set<string>();

    if (kind === 'sp-hub') {
      // s = sp id, t = hub id. Highlight this SP's patients flowing through
      // hub t (every hub now has downstream dots).
      sps.add(s);
      hubs.add(t);
      this.dots.filter(d => d.sp === s && d.hub === t).forEach(d => {
        fills.add(d.fillGroup);
        lanes.add(d.lane);
      });
      return { sps, hubs, fills, lanes, dotMatch: dd => dd.sp === s && dd.hub === t };
    }

    if (kind === 'hub-fill') {
      // s = hub id, t = fill id.
      hubs.add(s);
      fills.add(t);
      this.chartData.sp_hub.filter(l => l.t === s).forEach(l => sps.add(l.s));
      this.chartData.lanes.filter(l => l.fillGroup === t).forEach(l => lanes.add(l.id));
      return { sps, hubs, fills, lanes, dotMatch: dd => dd.fillGroup === t && dd.hub === s };
    }

    if (kind === 'fill-lane') {
      // s = fill id, t = lane id. Hubs that reach this lane come from its dots.
      fills.add(s);
      lanes.add(t);
      this.dots.filter(d => d.lane === t).forEach(d => {
        sps.add(d.sp);
        hubs.add(d.hub);
      });
      return { sps, hubs, fills, lanes, dotMatch: dd => dd.lane === t };
    }

    if (kind === 'hub-lane') {
      // Bridge role unbound — s = hub id, t = lane id.
      hubs.add(s);
      lanes.add(t);
      this.chartData.sp_hub.filter(l => l.t === s).forEach(l => sps.add(l.s));
      this.dots.filter(d => d.lane === t && d.hub === s).forEach(d => sps.add(d.sp));
      return { sps, hubs, fills, lanes, dotMatch: dd => dd.lane === t && dd.hub === s };
    }
    return null;
  }

  private applyFilters() {
    this.applyRouteHighlight(this.getActiveRoute());
  }

  /**
   * Apply opacities for a given route (or clear if null). Used by both
   * applyFilters (click-driven, persistent) and the link hover handlers
   * (transient — restored to applyFilters state on mouseleave).
   */
  private applyRouteHighlight(route: ReturnType<SankeyBeeswarmChartComponent['getActiveRoute']>) {
    const noFilter = !route;

    const BRIGHT_BAR  = 1;
    const DIM_BAR     = 0.15;
    const BRIGHT_LINK = 0.35;
    const DIM_LINK    = 0.03;
    const REST_LINK   = 0.18;
    const BRIGHT_DOT  = 0.9;
    const DIM_DOT     = 0.06;
    const REST_DOT    = 0.82;

    // Helper: extract the trailing id from a class token list, given a prefix.
    // e.g. for 'sp-bar sp-biologics', prefix='sp-' → 'biologics'.
    const idFromClass = (cls: string, prefix: string): string => {
      for (const c of cls.split(/\s+/)) {
        if (c.startsWith(prefix)) return c.slice(prefix.length);
      }
      return '';
    };

    // ── Dots ──
    // Pointer-events: dimmed dots are made fully inert so the browser also
    // disables the `cursor: pointer` + drop-shadow hover styling from the
    // SCSS. The JS handlers (mouseenter / mousemove / lasso) already gate
    // on the same predicate via getLegendDotMatch(), so this is defense in
    // depth — no event reaches them anyway.
    this.svg.selectAll('.dot')
      .attr('opacity', (dd: Dot) =>
        noFilter ? REST_DOT : (route!.dotMatch(dd) ? BRIGHT_DOT : DIM_DOT)
      )
      .style('pointer-events', (dd: Dot) =>
        noFilter ? null : (route!.dotMatch(dd) ? null : 'none')
      );

    // ── SP bars + labels ──
    this.svg.selectAll('.sp-bar')
      .style('opacity', function (this: any) {
        if (noFilter) return BRIGHT_BAR;
        const id = idFromClass(d3.select(this).attr('class') || '', 'sp-');
        return route!.sps.has(id) ? BRIGHT_BAR : DIM_BAR;
      });
    this.svg.selectAll('[class*="sp-label-"]')
      .style('opacity', function (this: any) {
        if (noFilter) return 1;
        const id = idFromClass(d3.select(this).attr('class') || '', 'sp-label-');
        return route!.sps.has(id) ? 1 : DIM_BAR;
      });

    // ── HUB bars + labels ──
    this.svg.selectAll('.hub-bar')
      .style('opacity', function (this: any) {
        if (noFilter) return BRIGHT_BAR;
        const id = idFromClass(d3.select(this).attr('class') || '', 'hub-');
        return route!.hubs.has(id) ? BRIGHT_BAR : DIM_BAR;
      });
    this.svg.selectAll('[class*="hub-label-"]')
      .style('opacity', function (this: any) {
        if (noFilter) return 1;
        const id = idFromClass(d3.select(this).attr('class') || '', 'hub-label-');
        return route!.hubs.has(id) ? 1 : DIM_BAR;
      });

    // ── Bridge bars + labels (fill groups: 'filled' / 'not_filled') ──
    this.svg.selectAll('.bridge-bar')
      .style('opacity', function (this: any) {
        if (noFilter) return BRIGHT_BAR;
        const id = idFromClass(d3.select(this).attr('class') || '', 'bridge-');
        return route!.fills.has(id) ? BRIGHT_BAR : DIM_BAR;
      });
    this.svg.selectAll('[class*="bridge-label-"]')
      .style('opacity', function (this: any) {
        if (noFilter) return 1;
        const id = idFromClass(d3.select(this).attr('class') || '', 'bridge-label-');
        return route!.fills.has(id) ? 1 : DIM_BAR;
      });

    // ── Lane stub bars (per outcome) ──
    this.svg.selectAll('.lane-bar')
      .style('opacity', function (this: any) {
        if (noFilter) return BRIGHT_BAR;
        // class includes 'lane-bar-{id}'
        const id = idFromClass(d3.select(this).attr('class') || '', 'lane-bar-');
        return route!.lanes.has(id) ? BRIGHT_BAR : DIM_BAR;
      });

    // ── Swim-lane backgrounds ──
    this.svg.selectAll('.lane')
      .style('opacity', function (this: any) {
        const base = 0.45;
        if (noFilter) return base;
        const id = idFromClass(d3.select(this).attr('class') || '', 'lane-');
        return route!.lanes.has(id) ? base : 0.1;
      });

    // ── Outcome labels above each lane stub ──
    this.svg.selectAll('.outcome-label')
      .style('opacity', function (this: any) {
        if (noFilter) return 1;
        // Prefix is `outcome-label-` (not `outcome-`) so idFromClass
        // doesn't latch onto the wrapper `outcome-label` token first and
        // come back with id="label" — that bug dimmed every lane label
        // regardless of the route.
        const id = idFromClass(d3.select(this).attr('class') || '', 'outcome-label-');
        return route!.lanes.has(id) ? 1 : DIM_BAR;
      });

    // ── Sankey links — match the link's actual (source, target) ids against the route ──
    this.svg.selectAll('.sankey-link')
      .attr('opacity', function (this: any) {
        if (noFilter) return REST_LINK;
        const el = d3.select(this);
        const s    = el.attr('data-link-s') || '';
        const t    = el.attr('data-link-t') || '';
        const kind = el.attr('data-link-kind') || '';

        if (kind === 'sp-hub')    return route!.sps.has(s)   && route!.hubs.has(t)  ? BRIGHT_LINK : DIM_LINK;
        if (kind === 'hub-fill')  return route!.hubs.has(s)  && route!.fills.has(t) ? BRIGHT_LINK : DIM_LINK;
        if (kind === 'fill-lane') return route!.fills.has(s) && route!.lanes.has(t) ? BRIGHT_LINK : DIM_LINK;
        return REST_LINK;
      });
  }

  /* ──────────── Node tooltip (SP / HUB / BRIDGE / LANE / sankey link) ────────────
   * Routed through DynamicCustomTooltipService (CDK Overlay) so positioning
   * stays correct inside scrolled containers, fullscreen, etc. — the previous
   * fixed-position div drifted because window.clientX/Y don't account for
   * scrolled parents. Same plumbing as the bubble tooltip.
   */
  private showTooltip(ev: MouseEvent, data: NonNullable<SankeyBeeswarmChartComponent['nodeTooltipData']>) {
    this.zone.run(() => {
      this.nodeTooltipData = data;
      this.cdr.detectChanges();
      this.dynamicTooltipService.createMouseTooltip(this.nodeTooltipTpl, this.viewContainerRef, ev);
    });
  }

  private moveTooltip(ev: MouseEvent) {
    this.dynamicTooltipService.updateTooltipPosition(this.nodeTooltipTpl, this.viewContainerRef, ev);
  }

  private hideTooltip() {
    this.dynamicTooltipService.hideMouseTooltip();
    this.nodeTooltipData = null;
  }
}
